import React, { useState, useEffect } from "react";
import { format, addDays, parseISO, isAfter, isBefore,startOfToday } from "date-fns";
import { Calendar,Eye, Bell, Mail, Share2, Upload, Download, Edit, CheckCircle, Tag, AlertCircle, Phone, Building, Clock, X, FileText, User, Users, MapPin, Paperclip, Image } from "lucide-react";
import { useLanguage } from '../StoreContext/LanguageContext';
import apiService from "../config";
import Section11Notice from "./HearingNotice";
import html2pdf from "html2pdf.js";

// Enhanced Hearing Component with improved design but original functionality preserved
export default function Hearing({ objectionId, objectionType, hearingData }) {

  // ----- Context & Initialization (PRESERVED EXACTLY) -----
  const { language, translations } = useLanguage();
  const today = format(new Date(), 'dd-MM-yyyy');

  // Retrieve objection data from localStorage if props are not provided
  const getObjectionData = () => {
    try {
      const storedObjection = localStorage.getItem('selectedObjection');
      return storedObjection ? JSON.parse(storedObjection) : null;
    } catch (error) {
      console.error('Error parsing stored objection:', error);
      return null;
    }
  };

  const storedObjection = getObjectionData();
  const fullData = storedObjection?.fullObjectionData || {};
  const rawType = fullData?.type || ""; console.log("🔎 storedObjection.fullObjectionData:", fullData);
  const hasPobjection = fullData?.pobjection_id != null;

  const getObjectionId = () => {
    // First try explicit props
    if (objectionId) return objectionId;
    if (hearingData?.objectionId) return hearingData.objectionId;
    if (storedObjection?.objectionId) return storedObjection.objectionId;

    // Then try fullData with proper null/undefined checks
    if (fullData) {
      // For private objections, prefer pobjection_id
      if (fullData.pobjection_id != null && fullData.pobjection_id !== undefined) {
        return fullData.pobjection_id;
      }
      // For general objections, use objection_id
      if (fullData.objection_id != null && fullData.objection_id !== undefined) {
        return fullData.objection_id;
      }
      // Last fallback to id field
      if (fullData.id != null && fullData.id !== undefined) {
        return fullData.id;
      }
    }

    return null;
  };

  // Determine final objection ID and type from props, hearingData, or localStorage
  // Prefer pobjection_id over objection_id when both exist
  const objId = getObjectionId();


  const objType = objectionType ||
    hearingData?.objectionType ||
    (rawType.toLowerCase() === "private" ? "Private" : "General");



  // Debug logs
  console.log('Stored objection:', storedObjection);
  console.log('Extracted values:', { objId, objType });

  // Determine final objection ID and type from props, hearingData, or localStorage
  // const objId = objectionId ||
  //   hearingData?.objectionId ||
  //   storedObjection?.objectionId ||
  //   storedObjection?.fullObjectionData?.id ||
  //   storedObjection?.fullObjectionData?.objection_id ||
  //   storedObjection?.fullObjectionData?.pobjection_id ||
  //   null;

  // const objType = objectionType ||
  //   hearingData?.objectionType ||
  //   storedObjection?.objectionType ||
  //   storedObjection?.fullObjectionData?.type ||
  //   (storedObjection?.fullObjectionData?.objection_id ? 'General' :
  //    storedObjection?.fullObjectionData?.pobjection_id ? 'Private' : 'General');

  // // Debug logs
  // console.log('Stored objection:', storedObjection);
  // console.log('Extracted values:', { objId, objType });

  // ----- State Declarations (PRESERVED EXACTLY) -----
  const [hasHearingOutcome, setHasHearingOutcome] = useState(false);
  const [displayObjectionDate, setDisplayObjectionDate] = useState("");
  const [objectionData, setObjectionData] = useState(null);
  const [existingHearings, setExistingHearings] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);

  const [isEditing, setIsEditing] = useState(false);
  const [editingHearing, setEditingHearing] = useState(null);

  const [isSendingNotice, setIsSendingNotice] = useState(false);
  const [shareModalSource, setShareModalSource] = useState(""); // values: "completed", "scheduled", etc.

  const maxDate = format(addDays(new Date(), 60), 'dd-MM-yyyy');

  // Form-related state
  // const [formData, setFormData] = useState({
  //   objection_id: String(objId || ""),
  //   objection_type: objType || "General",
  //   objection_date: "",
  //   hearingDate: today,
  //   HearingTime: "10:00",
  //   HearingLocation: "",
  //   ObjectionDetails: storedObjection?.fullObjectionData?.objection_details || "",
  //   HearingStatus: "Select Status",
  //   Remarks: "",
  //   OfficerName: "",
  //   OfficerDesignation: ""
  // });

  const [files, setFiles] = useState({});
  const [uploadedDocuments, setUploadedDocuments] = useState([]);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [fileSelected, setFileSelected] = useState(false);
  const [fileUploaded, setFileUploaded] = useState(false);
  const [fileName, setFileName] = useState("");

  // Control & UI state
  const [finalNoticeGenerated, setFinalNoticeGenerated] = useState(false);
  const [showNoticeModal, setShowNoticeModal] = useState(false);
  const [noticeText, setNoticeText] = useState("");

  const [activeHearing, setActiveHearing] = useState(false);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [shareEmail, setShareEmail] = useState("");
  const [sharePhone, setSharePhone] = useState("");

  // Tab management
  const [activeTab, setActiveTab] = useState(hearingData ? "upcoming" : "new");

  // Viewing & submission flags
  const [viewHearing, setViewHearing] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showViewCompletedModal, setShowViewCompletedModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  // Hearings lists
  const [hearingsList, setHearingsList] = useState({
    upcoming: [],
    completed: [],
    ongoing: []
  });
  const [existingHearingRecords, setExistingHearingRecords] = useState([]);

  // Final objection ID/Type (for fetch fallback)
  const [finalObjId, setFinalObjId] = useState(null);
  const [finalObjType, setFinalObjType] = useState(null);

  // Compensation example state (if needed)
  const [compensation, setCompensation] = useState({
    amount: '',
    date: ''
  });

  //Helper Functions (PRESERVED EXACTLY)

  // Check if main form is valid
  const isFormValid = () => {
    // First check if HearingStatus is valid
    if (!formData.HearingStatus ||
      formData.HearingStatus === "" ||
      formData.HearingStatus === "Select Status" ||
      !["Scheduled", "Completed"].includes(formData.HearingStatus)) {
      return false;
    }
    if (formData.HearingStatus === 'Completed') {
      // Check if final decision is provided
      if (!formData.final_decision || formData.final_decision.trim() === '') {
        return false;
      }

      // Check if at least one document is marked as final verdict
      const hasFinalVerdict = uploadedDocuments.some(doc => doc.isFinalVerdict);
      if (!hasFinalVerdict) {
        return false;
      }
    }
    return (
      formData.objection_id.trim() !== '' &&
      formData.hearingDate !== '' &&
      formData.HearingTime !== '' &&
      formData.HearingLocation.trim() !== '' &&
      // formData.ObjectionDetails.trim() !== '' &&
      // formData.objection_date.trim() !== '' &&
      formData.Remarks.trim() !== '' &&
      formData.OfficerName.trim() !== '' &&
      formData.OfficerDesignation.trim() !== ''
      // formData.HearingStatus.trim() !=='' 
    );
  };

  // Check if notice can be generated
  const canGenerateNotice = () => {
    return (
      formData.hearingDate !== '' &&
      formData.HearingLocation.trim() !== '' &&
      formData.OfficerName.trim() !== ''
    );
  };

  // Format API date string to dd-MM-yyyy
  const formatApiDate = (dateString) => {
    if (!dateString || dateString === "1900-01-01T00:00:00.000Z") return '';
    return format(new Date(dateString), 'dd-MM-yyyy');
  };

  // Format API time string to HH:mm
  const formatApiTime = (timeString) => {
    if (!timeString) return '';
    return format(new Date(timeString), 'HH:mm');
  };

  // Transform raw hearing data and attach its associated documents
  // const transformHearingDataWithDocs = (hearing, allDocuments) => {
  //   const hearingId = hearing.hearing_id || hearing.HearingID;
  //   const hearingDocuments = allDocuments.filter(doc =>
  //     doc.ref_table_name === 'ObjectionHearings' &&
  //     doc.upload_path.includes(`${hearingId}_Hearing_`)
  //   );
  //   return {
  //     ...transformHearingData(hearing, hearing),
  //     documents: hearingDocuments
  //   };
  // };
  const transformHearingDataWithDocs = (hearing, allDocuments, objection = null) => {
    const hearingId = hearing.hearing_id || hearing.HearingID;
    const hearingDocuments = allDocuments.filter(doc =>
      doc.ref_table_name === 'ObjectionHearings' &&
      doc.upload_path.includes(`${hearingId}_Hearing_`)
    );
    return {
      ...transformHearingData(hearing, objection),
      documents: hearingDocuments
    };
  };

  // Core transformer: normalize API hearing object
  const transformHearingData = (hearing, objection = null) => {
    console.log("🚀 SETTING FORM DATA");
    console.log("🔍 objection.objection_details:", objection.objection_details);
    console.log("🔍 Length:", objection.objection_details?.length);

    return {
      id: hearing.hearing_id || hearing.id,
      // caseNumber: hearing.case_number || `LAQ${new Date().getFullYear()}${hearing.objection_id}`,
      // caseNumber: hearing.case_number || `LAQ${new Date().getFullYear()} ${hearing.pobjection_id ? 'P' : 'G'}${hearing.pobjection_id || hearing.objection_id}`,
      // caseNumber: hearing.case_number || `LAQ${new Date().getFullYear()}${objection?.pobjection_id ? 'P' : 'G'}${objection?.pobjection_id || objection?.objection_id}`,
      caseNumber: objection?.case_no || hearing.case_number || `LAQ${new Date().getFullYear()}${objection?.pobjection_id ? 'P' : 'G'}${objection?.pobjection_id || objection?.objection_id}`,

      //LAQ{objection.createdAt ? new Date(objection.createdAt).getFullYear() : new Date().getFullYear()}{objection.objection_id},
      date: hearing.HearingDate ? format(new Date(hearing.HearingDate), 'dd-MM-yyyy') : '',
      place: hearing.HearingLocation || hearing.hearing_location,
      status: hearing.HearingStatus || hearing.hearing_status,
      designatedTo: hearing.OfficerName || hearing.officer_name,
      caseIncharge: hearing.OfficerName || hearing.officer_name,
      caseDetails: hearing.ObjectionDetails || hearing.objection_details || "Case details",
      objection_date: objection?.objection_date || hearing.objection_date,
      decision: hearing.decision || hearing.final_decision,
      remarks: hearing.Remarks || hearing.remarks || '',
      landOwners: hearing.land_owners || [
        {
          name: hearing.landowner_name || "To be updated",
          share: "100%",
          contact: hearing.landowner_contact || "email@example.com"
        }
      ],
      hearingSummary: hearing.hearing_summary
        ? (Array.isArray(hearing.hearing_summary)
          ? hearing.hearing_summary
          : [hearing.hearing_summary])
        : [],
      nextSteps: hearing.next_steps || "Pending review",
      finalVerdict: hearing.final_verdict || hearing.final_decision,
      firstHearing: hearing.first_hearing || false,
      totalHearings: hearing.total_hearings || 1,
      originalData: hearing
    };
  };

  // ORIGINAL API FETCH FUNCTION (PRESERVED EXACTLY)
  // const fetchHearingData = async () => {
  //   if (!objId || !objType) {
  //     console.warn('Missing objectionId or objectionType');
  //     return;
  //   }
  //   setLoading(true);
  //   try {
  //     const response = await apiService.getHearing(objId, objType);
  //     console.log("Raw API response:", response);
  //     const { objection, hearing, documents } = response;

  //     setObjectionData(objection);
  //     setDocuments(documents || []);

  //     // Check if hearing_outcome exists and has value
  //     const hearingOutcomeExists = objection?.hearing_outcome && 
  //                                 objection.hearing_outcome.trim() !== '';
  //     setHasHearingOutcome(hearingOutcomeExists);

  //     // Set active tab based on hearing_outcome
  //     if (hearingOutcomeExists) {
  //       setActiveTab("completed");
  //     } else {
  //       setActiveTab(hearingData ? "upcoming" : "new");
  //     }

  //     if (hearing && hearing.length > 0) {
  //       const categorizedHearings = {
  //         upcoming: hearing.filter(h => h.HearingStatus === 'Scheduled'),
  //         ongoing: hearing.filter(h => h.HearingStatus === 'Pending'),
  //         completed: hearing.filter(h => h.HearingStatus === 'Completed')
  //       };

  //       const transformedHearings = {
  //         upcoming: categorizedHearings.upcoming.map(h => transformHearingDataWithDocs(h, documents)),
  //         ongoing: categorizedHearings.ongoing.map(h => transformHearingDataWithDocs(h, documents)),
  //         completed: categorizedHearings.completed.map(h => transformHearingDataWithDocs(h, documents))
  //       };

  //       setHearingsList(transformedHearings);
  //       setExistingHearings(hearing);
  //       console.log("Transformed hearingsList:", transformedHearings);
  //     }

  //     if (objection) {
  //       setDisplayObjectionDate(formatApiDate(objection.objection_date));
  //       setFormData(prev => ({
  //         ...prev,
  //         objection_id: String(objection.objection_id || objection.id),
  //         objection_type: objType,
  //         ObjectionDetails: objection.objection_details,
  //         HearingLocation: objection.hearing_venue || "",
  //         OfficerName: objection.hearing_officer || "",
  //         OfficerDesignation: objection.hearing_designation || "",
  //         Remarks: objection.remarks || "",
  //         HearingTime: objection.hearing_time || "10:00",
  //         hearingDate: objection.hearing_date
  //           ? format(new Date(objection.hearing_date), 'yyyy-MM-dd')
  //           : today
  //       }));
  //     }
  //   } catch (error) {
  //     console.error('Error fetching hearing data:', error);
  //     alert('Failed to fetch hearing data. Please try again.');
  //   } finally {
  //     setLoading(false);
  //   }
  // };
  console.log("✅ SENDING API request with:");
  console.log("objection_id:", objId);
  console.log("objection_type:", objType);

// COMPLETE FIX: Replace your entire fetchHearingData function with this

const fetchHearingData = async () => {
  if (!objId || !objType) {
    console.warn('Missing objectionId or objectionType');
    return;
  }
  setLoading(true);
  try {
    const response = await apiService.getHearing(objId, objType);
    console.log("Raw API response:", response);
    const { objection, hearing, documents } = response;

    setObjectionData(objection);
    setDocuments(documents || []);

    // Check if hearing_outcome exists and has value
    const hearingOutcomeExists = objection?.hearing_outcome &&
      objection.hearing_outcome.trim() !== '';
    setHasHearingOutcome(hearingOutcomeExists);

    // Set active tab based on hearing_outcome
    if (hearingOutcomeExists) {
      setActiveTab("completed");
    } else {
      setActiveTab(hearingData ? "upcoming" : "new");
    }

    // Process hearing data first
    let latestHearingData = {};
    if (hearing && hearing.length > 0) {
      const categorizedHearings = {
        upcoming: hearing.filter(h => h.HearingStatus === 'Scheduled'),
        ongoing: hearing.filter(h => h.HearingStatus === 'Pending'),
        completed: hearing.filter(h => h.HearingStatus === 'Completed')
      };

      const transformedHearings = {
        upcoming: categorizedHearings.upcoming.map(h => transformHearingDataWithDocs(h, documents, objection)),
        ongoing: categorizedHearings.ongoing.map(h => transformHearingDataWithDocs(h, documents)),
        completed: categorizedHearings.completed.map(h => transformHearingDataWithDocs(h, documents, objection))
      };

      setHearingsList(transformedHearings);
      setExistingHearings(hearing);

      // Get latest hearing for prefilling location and officer details
      const latestHearing = [...hearing]
        .sort((a, b) => new Date(b.HearingDate) - new Date(a.HearingDate))[0];

      if (latestHearing) {
        latestHearingData = {
          HearingLocation: latestHearing.HearingLocation || "",
          OfficerName: latestHearing.OfficerName || "",
          OfficerDesignation: latestHearing.OfficerDesignation || "",
        };
      }
    }

    // NOW set all form data in one go - this is the key fix
    if (objection) {
      console.log("🔍 Setting objection data:", objection);
      console.log("🔍 Objection details from API:", objection.objection_details);
      console.log("🔍 Latest hearing data:", latestHearingData);
      
      setDisplayObjectionDate(formatApiDate(objection.objection_date));
      
      // Set ALL form data at once to avoid conflicts
      const newFormData = {
        objection_id: String(objection.pobjection_id ?? objection.objection_id ?? objection.id ?? ""),
        objection_type: objType,
        objection_date: formatApiDate(objection.objection_date) || "",
        hearingDate: objection.hearing_date
          ? format(new Date(objection.hearing_date), 'yyyy-MM-dd')
          : today,
        HearingTime: objection.hearing_time || "10:00",
        HearingLocation: latestHearingData.HearingLocation || objection.hearing_venue || "",
        ObjectionDetails: objection.objection_details || "", // This should work now
        HearingStatus: "Select Status",
        Remarks: objection.remarks || "",
        OfficerName: latestHearingData.OfficerName || objection.hearing_officer || "",
        OfficerDesignation: latestHearingData.OfficerDesignation || objection.hearing_designation || ""
      };
      
      console.log("🚀 Setting complete form data:", newFormData);
      console.log("🔍 ObjectionDetails in form data:", newFormData.ObjectionDetails);
      console.log("🔍 ObjectionDetails length:", newFormData.ObjectionDetails?.length);
      
      setFormData(newFormData);
    }

  } catch (error) {
    console.error('Error fetching hearing data:', error);
    alert('Failed to fetch hearing data. Please try again.');
  } finally {
    setLoading(false);
  }
};

// ALSO ADD: Make sure your initial formData state includes ObjectionDetails properly
// In your useState declaration, make sure ObjectionDetails is initialized:

const [formData, setFormData] = useState({
  objection_id: String(objId || ""),
  objection_type: objType || "General",
  objection_date: "",
  hearingDate: today,
  HearingTime: "10:00",
  HearingLocation: "",
  ObjectionDetails: "", // Make sure this is initialized as empty string, not undefined
  HearingStatus: "Select Status",
  Remarks: "",
  OfficerName: "",
  OfficerDesignation: ""
});
  // Handle input changes for formData (PRESERVED EXACTLY)
  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Handle file selection per field (PRESERVED EXACTLY)
  const handleFileChange = (fileType, file) => {
    setFiles(prev => ({
      ...prev,
      [fileType]: file
    }));
    if (file) {
      setFileSelected(true);
      setFileName(file.name);
    }
  };

  // Document upload: add to uploadedDocuments array (PRESERVED EXACTLY)
  const handleDocumentUpload = (e) => {
    const filesArray = Array.from(e.target.files);
    filesArray.forEach(file => {
      if (file.size > 10 * 1024 * 1024) { // 10MB limit
        alert(`File ${file.name} is too large. Maximum size is 10MB.`);
        return;
      }
      const newDoc = {
        file: file,
        customName: '',
        uploadedAt: new Date()
      };
      setUploadedDocuments(prev => [...prev, newDoc]);
    });

    // Simulate upload progress
    setUploadProgress(0);
    const interval = setInterval(() => {
      setUploadProgress(prev => {
        if (prev >= 100) {
          clearInterval(interval);
          setFileUploaded(true);
          return 100;
        }
        return prev + 10;
      });
    }, 200);
  };

  // Handle changing document custom name (PRESERVED EXACTLY)
  const handleDocumentNameChange = (index, newName) => {
    setUploadedDocuments(prev =>
      prev.map((doc, i) =>
        i === index ? { ...doc, customName: newName } : doc
      )
    );
  };

  // Remove a document from the uploadedDocuments list (PRESERVED EXACTLY)
  const removeDocument = (index) => {
    setUploadedDocuments(prev => prev.filter((_, i) => i !== index));
    // If this was the only document, reset upload indicators
    if (uploadedDocuments.length === 1) {
      setFileUploaded(false);
      setUploadProgress(0);
    }
  };

  const handleFinalVerdictToggle = (index, isChecked) => {
    const updatedDocs = [...uploadedDocuments];

    // First, remove final verdict flag from all documents
    updatedDocs.forEach(doc => {
      doc.isFinalVerdict = false;
      if (doc.customName === 'Final Verdict') {
        doc.customName = '';
      }
    });

    // Then set the selected document as final verdict
    if (isChecked) {
      updatedDocs[index].isFinalVerdict = true;
      updatedDocs[index].customName = 'Final Verdict';
    }

    setUploadedDocuments(updatedDocs);
  };

  // Validation function for completed hearing (PRESERVED EXACTLY)
  const validateCompletedHearing = () => {
    if (editingHearing?.HearingStatus === 'Completed') {
      // Check if final decision is provided
      if (!editingHearing.final_decision || editingHearing.final_decision.trim() === '') {
        alert('Final Decision is required when completing a hearing.');
        return false;
      }

      // Check if Final Verdict document is uploaded
      const hasFinalVerdict = uploadedDocuments.some(doc => doc.isFinalVerdict) ||
        (viewHearing.documents && viewHearing.documents.some(doc => doc.document_name === 'Final Verdict'));

      if (!hasFinalVerdict) {
        alert('Final Verdict document is required when completing a hearing.');
        return false;
      }
    }

    return true;
  };

  // Handle date change with validation (PRESERVED EXACTLY)
  const handleDateChange = (e) => {
    const selectedDate = e.target.value;
    const parsedDate = parseISO(selectedDate);
    const currentDate = new Date();
    const maxAllowedDate = addDays(currentDate, 60);

    // if (isBefore(parsedDate, currentDate)) {
  //   if (isBefore(parsedDate)) {
  //     alert("Cannot select a date in the past");
  //     return;
  //   }
  //   if (isAfter(parsedDate, maxAllowedDate)) {
  //     alert("Cannot select a date more than 60 days in the future");
  //     return;
  //   }
  //   handleInputChange('hearingDate', selectedDate);
  // };

     const parsed = new Date(parsedDate);
  const today = startOfToday(); // sets time to 00:00:00
  
  if (isBefore(parsed, today)) {
    alert("❌ Cannot select a date in the past");
    return;
  }
  
  if (isAfter(parsed, maxAllowedDate)) {
    alert("❌ Cannot select a date more than 60 days in the future");
    return;
  }
      handleInputChange('hearingDate', selectedDate);
    };
  // Generate notice text and open the modal (PRESERVED EXACTLY)
  // const handleGenerateNotice = () => {
  //   const text = `\n\n\tGovernment of Maharashtra\n\tOffice of the Land Acquisition Officer\n\tBuldhana District\n\nSubject: Hearing Notice\n\nThis is to inform you that a hearing has been scheduled on ${formData.hearingDate} at ${formData.HearingLocation}. You are hereby summoned to appear before the undersigned regarding your objection.\n\nFailure to appear may result in ex parte decisions.\n\nSincerely,\nLand Acquisition Officer, Buldhana.`;
  //   setNoticeText(text);
  //   setShowNoticeModal(true);
  // };
  const handleGenerateNotice = () => {
    if (!isFormValid()) {
      alert("⚠️ Please complete all required fields before generating notice.");
      return;
    }
    const text = `


\t\t\t\tभूमी संपादन करताना उचित भरपाई मिळण्याचा आणि पारदर्शकतेचा हक्क अधिनियम 2013 चे कलम 
\t\t\t\t11(1)अंतर्गत जाहीर केलेल्या प्राथमिक अधिसूचनेवरील दाखल आक्षेपांची सुनावणी

\t\t\t\t\t\t\t\tनोटीस

अ.क्र. ${formData.objection_id}
${formData.fileNumber}
${formData.department}
${formData.office}
दिनांक:- ${formData.objection_date}


प्रति,

\t${formData.objector_name}
\t${formData.address}


विषय :- भूसंपादन प्रकरण क्रमांक ${formData.caseNumber} मोजे ${formData.village} ता. ${formData.taluka} जि. ${formData.district} प्रकरणामध्ये कलम 11 वरील हरकतीची सुनावणी

संदर्भ :- कलम 11(1)च्या प्राथमिक अधिसूचनेवरील आपला हरकत अर्ज

\tआपणास यास्द्वारे सूचित करण्यात येते की, विषयांकित भूसंपादन प्रकरणातील कलम 11(1)च्या प्राथमिक अधिसूचनेवर प्राप्त झालेल्या आपल्या हरकत अर्जावरील सुनावणीसाठी आपण स्वतः अथवा आपच्या अधिकृत वकिलामार्फत आपले म्हणणे मांडण्यासाठी माझ्या समक्ष दिनांक ${formData.hearingDate} ${formData.hearingMonth} ${formData.hearingYear} रोजी सकाळी ${formData.hearingTime} वाजता ${formData.venue} येथे योग्य त्या कागदपत्रांसह हजर राहावे. अन्यथा आपल्या गैरहजेरीत कार्यवाही करण्यात येईल.

हरकत अर्जावरील विवरण

भूसंपादन प्रकरण क्रमांक\t\t\tगट क्रमांक/ सर्वेक्षण\t\t\tक्षेत्रफळ
${formData.caseNumber} मोजे ${formData.village} ता. ${formData.taluka} जि.${formData.district}\t\t\t${formData.gateNumber}\t\t\t${formData.area}


\t\t\t\t\t\t\t\t\t\t\t\tउपजिल्हाधिकारी भूसंपादन
\t\t\t\t\t\t\t\t\t\t\t\t(मध्यम प्रकल्प) बुलढाणा


\t\t\t\t\t\t\t\tस्थळ भेट

अ जोडणी/२१ मे २०१८`;

    setNoticeText(text);
    setShowNoticeModal(true);
  };

  // Handle downloading notice (PRESERVED EXACTLY)
  // const handleDownload = () => {
  //   alert("Notice downloaded successfully.");
  // };
  const handleDownloadNotice = () => {
    const element = document.getElementById("printable-notice");
    if (!element) return alert("Notice content not found.");

    const opt = {
      margin: 0.5,
      filename: 'Hearing_Notice.pdf',
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2 },
      jsPDF: { unit: 'in', format: 'letter', orientation: 'portrait' }
    };

    html2pdf().from(element).set(opt).save();
  };

  // Handle scheduling a new hearing (PRESERVED EXACTLY)
  const handleFileSubmit = () => {
    setFileUploaded(true);
    setFileSelected(false);
    const newHearing = {
      id: Math.max(...hearingsList.upcoming.map(h => h.id), 0) + 1,
      date: formData.hearingDate,
      place: formData.HearingLocation,
      status: "Scheduled",
      designatedTo: formData.OfficerName,
      caseNumber: `LAQ/2025/NEW/${Math.floor(Math.random() * 1000)}`,
      caseDetails: "New land acquisition case (details pending)",
      landOwners: [
        { name: "To be added", share: "100%", contact: "email@example.com" }
      ],
      firstHearing: true,
      documents: [fileName]
    };

    setHearingsList(prev => ({
      ...prev,
      upcoming: [...prev.upcoming, newHearing]
    }));

    // Reset form fields
    setFormData({
      objection_id: String(objId || ""),
      objection_type: objType || "General",
      hearingDate: today,
      HearingTime: "10:00",
      HearingLocation: "",
      ObjectionDetails: storedObjection?.fullObjectionData?.objection_details || "",
      HearingStatus: "Pending",
      Remarks: "",
      OfficerName: "",
      OfficerDesignation: ""
    });
    setFileName("");
  };

  // ORIGINAL API SUBMISSION FUNCTION (PRESERVED EXACTLY)
//   const handleSubmitHearing = async (e) => {
//     if (!formData.HearingStatus || formData.HearingStatus === "") {
//       alert("Please select a hearing status");
//       return;
//     }
//     e.preventDefault();
//     if (!isFormValid()) {
//       alert('Please fill all required fields');
//       return;
//     }
//     setIsSubmitting(true);
//     try {
//       const submitData = new FormData();
//       Object.keys(formData).forEach(key => {
//         submitData.append(key, formData[key]);
//       });
//       if (uploadedDocuments && uploadedDocuments.length > 0) {
//         uploadedDocuments.forEach((doc, index) => {
//           const fieldName = doc.customName || 'Notice';
//           submitData.append(fieldName, doc.file);
//         });
//       }
//       console.log('Submitting hearing data:', Object.fromEntries(submitData));
//       const response = await apiService.createHearing(submitData);

//       if (response.status === 200) {
//         setSubmitSuccess(true);
//         const hearingId = response.data?.hearingId ||
//           response.data?.hearing_id ||
//           response.data?.id ||
//           response.hearingId ||
//           response.hearing_id ||
//           response.id ||
//           'Generated Successfully';
//         console.log('SUCCESS: Hearing creation completed');
//         // if (window.confirm(`Hearing scheduled successfully! Hearing ID: ${hearingId}\nClick OK to view upcoming hearings.`)) {
//         //   setActiveTab("upcoming");
//         //   console.log('Setting tab to upcoming');
//         // }
//         // const message = formData.HearingStatus === "Completed"
//         //   ? `✅ Final judgement declared successfully! Hearing ID: ${hearingId}`
//         //   : `Hearing scheduled successfully! Hearing ID: ${hearingId}`;

//         // // if (window.confirm(`${message}\nClick OK to view upcoming hearings.`)) {
//         // //   setActiveTab("upcoming");
//         // // }
//         if (response?.status === 200) {
//   const hearingId = response.data?.hearingId || response.data?.id || "Generated";
//   const message = formData.HearingStatus === "Completed"
//     ? `✅ Final judgement declared successfully! Hearing ID: ${hearingId}`
//     : `Hearing scheduled successfully! Hearing ID: ${hearingId}`;

//   const confirmed = window.confirm(`${message}\nClick OK to continue.`);
//         await fetchHearingData();
//   if (confirmed) {
//     setActiveTab("upcoming");
//   }
// } else {
//   alert("Something went wrong. Please try again.");
// }

//         setTimeout(() => {
//           resetForm();
//           setSubmitSuccess(false);
//         }, 3000);
//       }
//     } catch (error) {
//       console.error('Error creating hearing:', error);
//       alert('Failed to schedule hearing. Please try again.');
//       if (error.response?.data?.error) {
//         alert(`Failed to schedule hearing: ${error.response.data.error}`);
//       } else if (error.message) {
//         alert(`Failed to schedule hearing: ${error.message}`);
//       } else {
//         alert('Failed to schedule hearing. Please try again.');
//       }
//     } finally {
//       setIsSubmitting(false);
//     }
//   };
const handleSubmitHearing = async (e) => {
  e.preventDefault();

  if (!formData.HearingStatus || formData.HearingStatus === "") {
    alert("Please select a hearing status");
    return;
  }

  if (!isFormValid()) {
    alert('Please fill all required fields');
    return;
  }

  // ✅ IF Completed, skip generate + open Share Modal
  if (formData.HearingStatus === "Completed") {
    setShareModalSource("completed");
    setShareModalOpen(true);
    return; // ⛔ Stop the rest of the form submission
  }

  // ✅ ELSE — normal flow (Scheduled, etc.)
  handleGenerateNotice(); // ✅ Generate before submitting
  setIsSubmitting(true);

  try {
    const submitData = new FormData();

    Object.keys(formData).forEach(key => {
      submitData.append(key, formData[key]);
    });

    if (uploadedDocuments && uploadedDocuments.length > 0) {
      uploadedDocuments.forEach((doc, index) => {
        const fieldName = doc.customName || 'Notice';
        submitData.append(fieldName, doc.file);
      });
    }

    console.log('Submitting hearing data:', Object.fromEntries(submitData));
    const response = await apiService.createHearing(submitData);

    if (response.status === 200) {
      setSubmitSuccess(true);
      const hearingId =
        response.data?.hearingId ||
        response.data?.hearing_id ||
        response.data?.id ||
        response.hearingId ||
        response.hearing_id ||
        response.id ||
        'Generated Successfully';

      const message = `Hearing scheduled successfully! Hearing ID: ${hearingId}`;
      alert(message);

      await fetchHearingData();
      setActiveTab("upcoming");

      setTimeout(() => {
        resetForm();
        setSubmitSuccess(false);
      }, 3000);
    } else {
      alert("Something went wrong. Please try again.");
    }
  } catch (error) {
    console.error('Error creating hearing:', error);
    alert('Failed to schedule hearing. Please try again.');

    if (error.response?.data?.error) {
      alert(`Failed to schedule hearing: ${error.response.data.error}`);
    } else if (error.message) {
      alert(`Failed to schedule hearing: ${error.message}`);
    }
  } finally {
    setIsSubmitting(false);
  }
};



  // Reset the form to initial values (PRESERVED EXACTLY)
  const resetForm = () => {
    setFormData(prev => ({
      ...prev,
      hearingDate: today,
      HearingLocation: "",
      HearingStatus: "Select Status",
      Remarks: "",
    }));
    setFiles({ Notice: null, Legal: null });
    setFileSelected(false);
    setFileUploaded(false);
    setFileName("");
  };

  // ORIGINAL API UPDATE FUNCTION (PRESERVED EXACTLY)
  const handleUpdateStatus = async () => {
    if (!editingHearing) return;
    try {
      setIsSubmitting(true);
      // Fix date formatting - convert to ISO format (yyyy-MM-dd)
      let formattedDate = '';
      if (editingHearing.HearingDate) {
        const date = new Date(editingHearing.HearingDate);
        formattedDate = date.toISOString().split('T')[0]; // Gets yyyy-MM-dd
      }

      // Fix time formatting - extract only time portion
      let formattedTime = '';
      if (editingHearing.HearingTime || formData.HearingTime) {
        const timeValue = editingHearing.HearingTime || formData.HearingTime;
        if (typeof timeValue === 'string' && timeValue.includes('T')) {
          // If it's a full datetime string, extract time
          formattedTime = new Date(timeValue).toTimeString().slice(0, 5); // Gets HH:mm
        } else {
          // If it's already a time string
          formattedTime = timeValue;
        }
      }
      const updateData = {
        hearing_id: editingHearing.id,
        objection_id: formData.objection_id,
        objection_type: objType || "General",
        HearingStatus: editingHearing.status,
        HearingLocation: editingHearing.place,
        OfficerName: editingHearing.designatedTo,
        OfficerDesignation: editingHearing.originalData?.OfficerDesignation || '',
        Remarks: editingHearing.originalData?.Remarks || '',
        hearingDate: formattedDate,
        HearingTime: formattedTime,
        // Add final_decision if status is Completed
        ...(editingHearing.HearingStatus === 'Completed' && {
          final_decision: editingHearing.final_decision || ''
        })
      };
      const response = await apiService.createHearing(updateData);
      if (response.status === 200) {
        const hearingId = response.data?.hearingId ||
          response.data?.hearing_id ||
          response.data?.id ||
          response.hearingId ||
          response.hearing_id ||
          response.id ||
          'Generated Successfully';
        alert(`Hearing scheduled successfully! Hearing ID: ${hearingId}`);
        setIsEditing(false);
        setEditingHearing(null);
        await fetchHearingData();
        setShowViewModal(false);
      }
    } catch (error) {
      console.error('Error updating hearing:', error);
      alert('Failed to update hearing. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Fixed handleEditingChange function (PRESERVED EXACTLY)
  const handleEditingChange = (field, value) => {
    setEditingHearing(prev => {
      const updated = { ...prev, [field]: value };

      // Special handling for date fields
      if (field === 'HearingDate') {
        // Ensure date is in proper format
        updated.HearingDate = value;
      }

      // Special handling for time fields
      if (field === 'HearingTime') {
        // Ensure time is in proper format
        updated.HearingTime = value;
      }

      return updated;
    });
  };

  // Handle share action (PRESERVED EXACTLY)
  //   const handleShare = async (e) => {
  //     e.preventDefault();
  //     let emailSubject = "Hearing Notification";
  //     let emailBody = "";

  //     if (viewHearing) {
  //       emailSubject = `Hearing Notification: Case ${viewHearing.caseNumber}`;
  //       switch (viewHearing.status) {
  //         case "Scheduled":
  //           emailBody = `
  // Dear Concerned Parties,

  // This is to inform you that a hearing has been scheduled regarding case ${viewHearing.caseNumber}.

  // Case Details: ${viewHearing.caseDetails}
  // Date: ${viewHearing.date}
  // Location: ${viewHearing.place}
  // Designated Officer: ${viewHearing.designatedTo}

  // All concerned parties are requested to be present with relevant documents.

  // Regards,
  // Land Acquisition Office
  // Buldhana District
  //           `;
  //           break;
  //         case "In Progress":
  //           emailBody = `
  // Dear Concerned Parties,

  // This is an update regarding the ongoing hearing for case ${viewHearing.caseNumber}.

  // Case Details: ${viewHearing.caseDetails}
  // Next Hearing Date: ${viewHearing.date}
  // Location: ${viewHearing.place}
  // Designated Officer: ${viewHearing.designatedTo}

  // Progress so far:
  // ${viewHearing.hearingSummary.join('\n')}

  // Next steps: ${viewHearing.nextSteps}

  // All concerned parties are requested to be present with relevant documents.

  // Regards,
  // Land Acquisition Office
  // Buldhana District
  //           `;
  //           break;
  //         case "Completed":
  //           emailBody = `
  // Dear Concerned Parties,

  // This is to inform you about the completed hearing and final decision for case ${viewHearing.caseNumber}.

  // Case Details: ${viewHearing.caseDetails}
  // Final Hearing Date: ${viewHearing.date}
  // Location: ${viewHearing.place}

  // Final Verdict: ${viewHearing.finalVerdict}

  // Total hearings conducted: ${viewHearing.totalHearings}

  // If you have any questions regarding the decision, please contact the Land Acquisition Office.

  // Regards,
  // Land Acquisition Office
  // Buldhana District
  //           `;
  //           break;
  //         default:
  //           emailBody = "Hearing details notification";
  //       }
  //     }
  //      await handleSendNoticeMail();

  //     alert(`Email sent successfully to ${shareEmail || sharePhone} with attachment.`);
  //     setShareModalOpen(false);
  //     setShareEmail("");
  //     setSharePhone("");
  //   };
  const handleShare = async (e) => {
    e.preventDefault();
     setIsSendingNotice(true); // 🔁 Start loader

    const success = await handleSendNoticeMail();
    setIsSendingNotice(false); // ✅ Stop loader

    if (!success) return;

    // setShareModalOpen(false);
  };

  // Determine CSS badge for hearing status (ENHANCED DESIGN)
  const getStatusBadge = (status) => {
    const badges = {
      "Completed": "px-3 py-1.5 rounded-full bg-gradient-to-r from-green-100 to-green-200 text-green-800 text-xs font-semibold shadow-sm",
      "In Progress": "px-3 py-1.5 rounded-full bg-gradient-to-r from-blue-100 to-blue-200 text-blue-800 text-xs font-semibold shadow-sm",
      "Scheduled": "px-3 py-1.5 rounded-full bg-gradient-to-r from-amber-100 to-amber-200 text-amber-800 text-xs font-semibold shadow-sm",
      "Pending": "px-3 py-1.5 rounded-full bg-gradient-to-r from-orange-100 to-orange-200 text-orange-800 text-xs font-semibold shadow-sm"
    };
    return <span className={badges[status] || badges["Pending"]}>{status === "Pending" ? "Ongoing" : status}</span>;
  };

  // Handle clicking "View" on a hearing row (PRESERVED EXACTLY)
  // const handleViewHearing = (hearing) => {
  //   const fullHearing = {
  //     ...hearing,
  //     objection: objectionData,
  //     hearing: existingHearings,
  //     documents: documents,
  //     // selectedHearingId: hearing.HearingID || hearing.hearing_id || hearing.id
  //   };
  //   setViewHearing(fullHearing);
  //   setEditingHearing({ ...hearing }); // Create editable copy
  //   setIsEditing(false);
  //   setShowViewModal(true);
  // };
  const handleViewHearing = (hearing) => {
    const fullHearing = {
      ...hearing,
      objection: objectionData,
      hearing: existingHearings,
      documents: documents
    };

    // Determine email and phone from correct source
    const email =
      hearing?.objection?.objector_email ||
      hearing?.objection?.owner_email ||
      objectionData?.objector_email ||
      objectionData?.owner_email ||
      storedObjection?.fullObjectionData?.objector_email ||
      storedObjection?.fullObjectionData?.owner_email ||
      "";

    const phone =
      hearing?.objection?.objector_contact ||
      hearing?.objection?.owner_contact ||
      objectionData?.objector_contact ||
      objectionData?.owner_contact ||
      storedObjection?.fullObjectionData?.objector_contact ||
      storedObjection?.fullObjectionData?.owner_contact ||
      "";

    setViewHearing(fullHearing);
    setEditingHearing({ ...hearing });
    setIsEditing(false);
    setShareEmail(email);
    setSharePhone(phone);
    setShowViewModal(true);
  };


  // const getCurrentHearing = () => {
  //   return viewHearing?.hearing?.find(
  //     h => Number(h.HearingID) === Number(viewHearing.selectedHearingId)
  //   );
  // };

  const getCurrentHearing = () => {
    if (!viewHearing?.hearing || viewHearing.hearing.length === 0) return null;

    return [...viewHearing.hearing].sort((a, b) => {
      const dateA = new Date(`${a.HearingDate}T${new Date(a.HearingTime).toISOString().split("T")[1]}`);
      const dateB = new Date(`${b.HearingDate}T${new Date(b.HearingTime).toISOString().split("T")[1]}`);
      return dateB - dateA;
    })[0];
  };

  const current = getCurrentHearing();

  // Handle clicking "View" on a completed hearing row (PRESERVED EXACTLY)
  // const handleCompletedViewHearing = (hearing) => {
  //   const fullHearing = {
  //     ...hearing,
  //     objection: objectionData,
  //     hearing: existingHearings,
  //     documents: documents
  //   };
  //   setViewHearing(fullHearing);
  //   setEditingHearing({ ...hearing }); // Create editable copy
  //   setIsEditing(false);
  //   setShowViewCompletedModal(true); // This should be your completed modal state
  // };
  const handleCompletedViewHearing = (hearing) => {
    // Ensure the hearing is already transformed via transformHearingData
    const fullHearing = {
      ...hearing,
      objection: objectionData || storedObjection?.fullObjectionData || {},
      hearing: existingHearings,
      documents: hearing.documents || documents // fallback
    };

    const email = fullHearing.objection?.objector_email ||fullHearing.objection?.owner_email || "";
    const phone = fullHearing.objection?.objector_contact || fullHearing.objection?.owner_contact || "";

    console.log("✅ Completed View Hearing:", fullHearing); // Debug

    setViewHearing(fullHearing);
    setEditingHearing({ ...hearing });
    setIsEditing(false);
    setShareEmail(email);
    setSharePhone(phone);
    setShowViewCompletedModal(true);
  };


  const normalizeObjectionData = (objection) => {
    return {
      id: objection?.pobjection_id ?? objection?.objection_id ?? objection?.id,
      case_no: objection?.case_no ?? "",
      name: objection?.objector_name ?? objection?.owner_name ?? "",
      contact: objection?.objector_contact ?? objection?.owner_contact ?? "",
      email: objection?.objector_email ?? objection?.owner_email ?? "-",
      address: objection?.objector_address ?? objection?.owner_address ?? "",
      survey_no: objection?.survey_no ?? objection?.affected_survey_no ?? "",
      village: objection?.eng_village_name ?? "",
      taluka: objection?.eng_tehsil_name ?? "",
      district: objection?.eng_dist_name ?? "",
      aadhaar: objection?.identity_proof_no ?? "",
      id_type: objection?.identity_proof_type ?? "",
      type: objection?.pobjection_id ? "Private" : "General",
    };
  };

  // Helper function to get hearings to display (PRESERVED EXACTLY)
  const getHearingsToDisplay = (activeTab, hearingsList) => {
    const hearings = hearingsList[activeTab] || [];

    // If it's upcoming tab and there are hearings, return only the latest one
    if (activeTab === "upcoming" && hearings.length > 0) {
      // Method 1: Get hearing with highest HearingID
      const latestHearing = hearings.reduce((latest, current) =>
        current.HearingID > latest.HearingID ? current : latest
      );
      return [hearings[hearings.length - 1]];
    }

    // For other tabs, return all hearings
    return hearings;
  };

  const handleSendNoticeMail = async () => {
    try {
      const uploadedFile = files?.manualNotice;
      let finalFile = null;

      // 👇 If user uploaded a manual notice, use that
      if (uploadedFile) {
        finalFile = uploadedFile;
      } else {
        // 🔄 Fallback: generate PDF from noticeText
        if (!noticeText) {
          alert("⚠️ Please attach generated notice before sharing.");
          return false;
        }

        const element = document.getElementById("printable-notice");
        if (!element) {
          alert("⚠️ Notice content not found. Please generate it first.");
          return false;
        }

        const opt = {
          margin: 0.5,
          filename: `notice-${Date.now()}.pdf`,
          image: { type: 'jpeg', quality: 0.98 },
          html2canvas: { scale: 2 },
          jsPDF: { unit: 'in', format: 'letter', orientation: 'portrait' }
        };

        const pdfBlob = await html2pdf().from(element).set(opt).outputPdf('blob');
        finalFile = new File([pdfBlob], opt.filename, { type: 'application/pdf' });
      }

      const response = await apiService.sendHearingNoticeMail(objId, objType, finalFile);

      if (response.status === 200) {
        alert(`✅ Email sent successfully to ${shareEmail || sharePhone}`);
        return true;
      } else {
        alert("⚠️ Failed to send email. Try again.");
        return false;
      }
    } catch (error) {
      console.error("❌ Error sending notice email:", error);
      alert("❌ Failed to send notice. Check console.");
      return false;
    }
  };


  // ----- Effects (PRESERVED EXACTLY) -----
  useEffect(() => {
    if (!objectionData) return;
    setShareEmail(objectionData.objector_email || objectionData.owner_email || "");
    setSharePhone(objectionData.objector_contact || objectionData.owner_contact || "");
  }, [objectionData]);


  // Control "active hearing" flag based on formData
  useEffect(() => {
    if (formData.HearingLocation && formData.hearingDate && formData.OfficerName) {
      setActiveHearing(true);
    } else {
      setActiveHearing(false);
    }
  }, [formData.HearingLocation, formData.hearingDate, formData.OfficerName]);

  // Fetch hearing data on mount or when objId/objType change
  useEffect(() => {
    if (objId && objType) {
      console.log("Calling fetchHearingData with objId:", objId, "and objType:", objType);
      fetchHearingData();
      setFinalObjId(objId);
      setFinalObjType(objType);
    } else if (storedObjection) {
      const objIdFromStorage = storedObjection?.objectionId ||
        storedObjection?.fullObjectionData?.id ||
        storedObjection?.fullObjectionData?.objection_id ||
        storedObjection?.fullObjectionData?.pobjection_id;

      const objTypeFromStorage = storedObjection?.objectionType ||
        storedObjection?.fullObjectionData?.type ||
        (storedObjection?.fullObjectionData?.pobjection_id
          ? 'Private'
          : 'General');

      setFinalObjId(objIdFromStorage);
      setFinalObjType(objTypeFromStorage);
      fetchHearingData();
    }
  }, [objId, objType]);

  // Keep finalObjId/type in sync if storage changes
  useEffect(() => {
    if (storedObjection) {
      const objIdFromStorage = storedObjection?.objectionId ||
        storedObjection?.fullObjectionData?.id ||
        storedObjection?.fullObjectionData?.objection_id ||
        storedObjection?.fullObjectionData?.pobjection_id;

      const objTypeFromStorage = storedObjection?.objectionType ||
        storedObjection?.fullObjectionData?.type ||
        (storedObjection?.fullObjectionData?.pobjection_id
          ? 'Private'
          : 'General');

      setFinalObjId(objIdFromStorage);
      setFinalObjType(objTypeFromStorage);
      console.log("Set finalObjId/type from storedObjection:", objIdFromStorage, objTypeFromStorage);
    }
  }, [storedObjection]);

  const renderTabNavigation = () => {
    if (hasHearingOutcome) {
      // Only show completed tab if hearing_outcome exists
      return (
        <div className="flex border-b border-gray-200 mb-8">
          <button
            onClick={() => setActiveTab("completed")}
            className={`px-8 py-4 font-semibold text-sm border-b-3 transition-all duration-300 flex items-center space-x-2 ${activeTab === "completed"
              ? "border-green-500 text-green-600 bg-gradient-to-r from-green-50 to-green-100"
              : "border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50"
              }`}
          >
            <CheckCircle className="w-5 h-5" />
            <span>{translations[language].completedHearing}</span>
          </button>
        </div>
      );
    } else {
      // Show new and upcoming tabs if no hearing_outcome
      return (
        <div className="flex border-b border-gray-200 mb-8">
          <button
            onClick={() => setActiveTab("new")}
            className={`px-8 py-4 font-semibold text-sm border-b-3 transition-all duration-300 flex items-center space-x-2 ${activeTab === "new"
              ? "border-blue-500 text-blue-600 bg-gradient-to-r from-blue-50 to-blue-100"
              : "border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50"
              }`}
          >
            <Calendar className="w-5 h-5" />
            <span>{translations[language].scheduleNewHearing}</span>
          </button>
          <button
            onClick={() => setActiveTab("upcoming")}
            className={`px-8 py-4 font-semibold text-sm border-b-3 transition-all duration-300 flex items-center space-x-2 ${activeTab === "upcoming"
              ? "border-amber-500 text-amber-600 bg-gradient-to-r from-amber-50 to-amber-100"
              : "border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50"
              }`}
          >
            <Clock className="w-5 h-5" />
            <span>{translations[language].upcomingHearings}</span>
          </button>
        </div>
      );
    }
  };

  // Loading & Error Guards (PRESERVED EXACTLY)
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="bg-white rounded-2xl shadow-xl p-8 flex items-center space-x-4">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
          <span className="text-lg font-medium text-gray-700">{translations[language].loadingHearingData}</span>
        </div>
      </div>
    );
  }

  if (!objId) {
    console.error('No objection ID provided. Props received:', { objectionId, objectionType, hearingData });
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-800 mb-2">{translations[language].noObjectionSelected}</h2>
          <p className="text-gray-600">{translations[language].selectObjectionFirst}</p>
        </div>
      </div>
    );
  }
  const displayObjection = normalizeObjectionData(viewHearing?.objection);
  console.log("ViewHearing Objection:", viewHearing?.objection);
  console.log("P-ID:", viewHearing?.objection?.pobjection_id);
  // UI (JSX Return) - ENHANCED DESIGN WITH PRESERVED FUNCTIONALITY

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Enhanced Header */}
        <div className="bg-white rounded-3xl shadow-2xl overflow-hidden mb-8 border border-gray-200">
          <div className="bg-gradient-to-r from-blue-900 to-blue-900 text-white p-8">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold mb-2">{translations[language].hearingManagement}</h1>
              </div>
              {/* <div className="hidden md:flex items-center space-x-6">
                <div className="text-center">
                  <div className="text-2xl font-bold text-white">{getHearingsToDisplay(activeTab, hearingsList).length}</div>
                  <div className="text-indigo-200 text-sm">Active Cases</div>
                </div>
                <div className="w-px h-12 bg-indigo-600"></div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-white">{hearingsList.completed.length}</div>
                  <div className="text-indigo-200 text-sm">Completed</div>
                </div>
              </div> */}
            </div>
          </div>

          {/* Enhanced Tabs */}
          {renderTabNavigation()}
        </div>

        {/* Enhanced Hearing Lists */}
        {activeTab !== "new" && (
          <div className="bg-white rounded-3xl shadow-2xl overflow-hidden mb-8 border border-gray-200">
            <div className="p-8">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-800 flex items-center">
                  {activeTab === "upcoming" && <Clock className="w-6 h-6 mr-3 text-amber-500" />}
                  {activeTab === "completed" && <CheckCircle className="w-6 h-6 mr-3 text-green-500" />}
                  {activeTab === "upcoming" ? "Upcoming Hearings" : "Completed Hearings"}
                </h2>
              </div>

              <div className="overflow-x-auto">
                <table className="min-w-full">
                  <thead>
                    <tr className="bg-gradient-to-r from-gray-50 to-gray-100">
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider rounded-tl-xl">{translations[language].caseNumber}</th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">{translations[language].objectiondate}</th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">{translations[language].location}</th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">{translations[language].hearingdate}</th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">{translations[language].status}</th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">{translations[language].assignedTo}</th>
                      {activeTab === "completed" && <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">{translations[language].Decision}</th>}
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider rounded-tr-xl">{translations[language].actions}</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-100">
                    {getHearingsToDisplay(activeTab, hearingsList).map((hearing) => (
                      <tr key={hearing.id} className="hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50 transition-all duration-200">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-semibold text-gray-900">{hearing.caseNumber}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-600">
                            {objectionData?.objection_date ? formatApiDate(objectionData.objection_date) : 'N/A'}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900 flex items-center">
                            <MapPin className="w-4 h-4 mr-1 text-gray-400" />
                            {hearing.place}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900 flex items-center">
                            <Calendar className="w-4 h-4 mr-1 text-gray-400" />
                            {hearing.date}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">{getStatusBadge(hearing.status)}</td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900 flex items-center">
                            <User className="w-4 h-4 mr-1 text-gray-400" />
                            {hearing.designatedTo || hearing.caseIncharge}
                          </div>
                        </td>
                        {activeTab === "completed" && <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{hearing.decision}</td>}
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
  <div className="flex space-x-3 justify-center">
    {/* View Button */}
    <div className="relative group">
      <button
        onClick={() => {
          if (activeTab === "completed") {
            handleCompletedViewHearing(hearing);
          } else {
            handleViewHearing(hearing);
          }
        }}
        className="inline-flex items-center justify-center w-8 h-8 bg-gradient-to-r from-blue-600 to-blue-900 text-white rounded-lg hover:from-blue-900 hover:to-blue-900 transition-all duration-200 shadow-sm hover:shadow-md"
      >
        <Eye size={16} />
      </button>
      {/* Tooltip */}
      <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-gray-800 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap">
        {translations[language].view}
      </div>
    </div>

    {/* Share Button */}
    <div className="relative group">
      <button
        type="button"
        onClick={() => setShareModalOpen(true)}
        // disabled={!activeHearing}
        className={`inline-flex items-center justify-center w-8 h-8 rounded-lg transition-all duration-200 shadow-sm hover:shadow-md ${
          // !activeHearing
          //   ? 'bg-gray-300 cursor-not-allowed text-gray-500'
          //   : 
          'bg-gradient-to-r from-purple-600 to-pink-700 hover:from-purple-700 hover:to-pink-800 text-white'
        }`}
      >
        <Share2 size={16} />
      </button>
      {/* Tooltip */}
      <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-gray-800 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap">
        {translations[language].share}
      </div>
    </div>

    {/* Schedule New Hearing Button */}
    {activeTab !== "completed" && (
      <div className="relative group">
        <button
          onClick={() => {
            handleViewHearing(hearing);
            setShowViewModal(false);
            setActiveTab("new");
          }}
          className="inline-flex items-center justify-center w-8 h-8 bg-gradient-to-r from-amber-500 to-orange-600 text-white rounded-lg hover:from-amber-600 hover:to-orange-700 transition-all duration-200 shadow-sm hover:shadow-md"
        >
          <Edit size={16} />
        </button>
        {/* Tooltip */}
        <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-gray-800 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap">
          {translations[language].ScheduleNewHearing}
        </div>
      </div>
    )}
  </div>
</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Enhanced New Hearing Form */}
        {activeTab === "new" && (
          <form onSubmit={handleSubmitHearing} className="bg-white rounded-3xl shadow-2xl overflow-hidden border border-gray-200">
            <div className="bg-gradient-to-r from-blue-600 to-indigo-700 p-6">
              <h2 className="text-2xl font-bold text-white flex items-center">
                <Calendar className="w-6 h-6 mr-3" />
                {translations[language].ScheduleNewHearing}
              </h2>
            </div>

            <div className="p-8">
              {/* Success Message */}
              {submitSuccess && (
                <div className="mb-8 p-6 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-2xl flex items-center shadow-sm">
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mr-4">
                    <CheckCircle size={24} className="text-green-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-green-800">{translations[language].hearingScheduledSuccess}</h3>
                    <p className="text-green-600">{translations[language].hearingScheduledMessage}</p>
                  </div>
                </div>
              )}

              {/* Basic Information */}
              <div className="mb-8">
                <h3 className="text-xl font-semibold text-gray-800 mb-6 flex items-center">
                  <FileText className="w-5 h-5 mr-2 text-indigo-600" />
                  {translations[language].basicInformation}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  <div className="space-y-2">
                    <label className="block text-sm font-semibold text-gray-700">{translations[language].caseNumber}</label>
                    {/* <p className="w-full p-4 bg-gray-50 border border-gray-200 rounded-xl text-gray-800 font-semibold tracking-wide font-sans">
    {`LAQ${formData?.objection_date
    ? new Date(formData.objection_date).getFullYear()
    : new Date().getFullYear()}${formData.objection_type === 'Private' ? 'P' : 'G'}${formData.objection_id || ''}`}
  </p> */}
                    <p className="w-full p-4 bg-gray-50 border border-gray-200 rounded-xl text-gray-800 font-semibold tracking-wide font-sans">
                      {objectionData?.case_no || `LAQ${formData?.objection_date
                        ? new Date(formData.objection_date).getFullYear()
                        : new Date().getFullYear()}${formData.objection_type === 'Private' ? 'P' : 'G'}${formData.objection_id || ''}`}
                    </p>

                  </div>

                  <div className="space-y-2">
                    <label className="block text-sm font-semibold text-gray-700">{translations[language].objectionType}</label>
                    <input
                      type='text'
                      value={formData.objection_type === 'Private' ? 'Personal' : formData.objection_type}
                      disabled
                      onChange={(e) => handleInputChange('objection_type', e.target.value)}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all cursor-not-allowed"
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="block text-sm font-semibold text-gray-700">{translations[language].objectionRaisedDate}</label>
                    <div className="relative">
                      <Calendar className="absolute left-4 top-4 h-5 w-5 text-gray-400" />
                      <input
                        type="date"
                        required
                        value={objectionData?.objection_date ? format(new Date(objectionData.objection_date), 'yyyy-MM-dd') : ''}
                        readOnly
                        min={today}
                        max={maxDate}
                        onChange={handleDateChange}
                        className="w-full pl-12 p-4 bg-gray-50 border border-gray-200 rounded-xl text-gray-600 cursor-not-allowed font-medium"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Date, Time and Location */}
              <div className="mb-8">
                <h3 className="text-xl font-semibold text-gray-800 mb-6 flex items-center">
                  <Clock className="w-5 h-5 mr-2 text-indigo-600" />
                  {translations[language].hearingDetails}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  <div className="space-y-2">
                    <label className="block text-sm font-semibold text-gray-700">{translations[language].hearingStatus} *</label>
                    <select
                      value={formData.HearingStatus || ""}
                      required
                      onChange={(e) => handleInputChange('HearingStatus', e.target.value)}
                      className="w-full p-4 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all font-medium bg-white"
                    >
                      <option value="">{translations[language].selectStatus}</option>
                      <option value="Scheduled">{translations[language].statusScheduled}</option>
                      <option value="Completed">{translations[language].statusCompleted}</option>
                    </select>
                  </div>

                  <div className="space-y-2">
                    {/* <label className="block text-sm font-semibold text-gray-700">Hearing Date *</label> */}
                    <label className="block text-sm font-semibold text-gray-700">
                      {formData.HearingStatus === "Completed" ? "Judgement Date *" : "Hearing Date *"}
                    </label>

                    <div className="relative">
                      <Calendar className="absolute left-4 top-4 h-5 w-5 text-gray-400" />
                      <input
                        type="date"
                        required
                        value={formData.hearingDate}
                        min={today}
                        max={maxDate}
                        onChange={handleDateChange}
                        className="w-full pl-12 p-4 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all font-medium"
                      />
                    </div>
                    <p className="text-xs text-gray-500">{translations[language].dateWithin60Days}</p>
                  </div>

                  <div className="space-y-2">
                    <label className="block text-sm font-semibold text-gray-700">{translations[language].hearingTime} *</label>
                    <div className="relative">
                      <Clock className="absolute left-4 top-4 h-5 w-5 text-gray-400" />
                      <input
                        type="time"
                        required
                        value={formData.HearingTime}
                        onChange={(e) => handleInputChange('HearingTime', e.target.value)}
                        className="w-full pl-12 p-4 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all font-medium"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="block text-sm font-semibold text-gray-700">{translations[language].hearingLocation} *</label>
                    <div className="relative">
                      <MapPin className="absolute left-4 top-4 h-5 w-5 text-gray-400" />
                      <input
                        type="text"
                        required
                        value={formData.HearingLocation}
                        onChange={(e) => handleInputChange('HearingLocation', e.target.value)}
                        className="w-full pl-12 p-4 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all font-medium"
                        placeholder={translations[language].enterHearingLocation}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Officer Information */}
              <div className="mb-8">
                <h3 className="text-xl font-semibold text-gray-800 mb-6 flex items-center">
                  <User className="w-5 h-5 mr-2 text-indigo-600" />
                 {translations[language].officerInformation}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="block text-sm font-semibold text-gray-700">{translations[language].officerName} *</label>
                    <div className="relative">
                      <User className="absolute left-4 top-4 h-5 w-5 text-gray-400" />
                      <input
                        type="text"
                        required
                        value={formData.OfficerName}
                        onChange={(e) => handleInputChange('OfficerName', e.target.value)}
                        className="w-full pl-12 p-4 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all font-medium"
                        placeholder={translations[language].enterOfficerName}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="block text-sm font-semibold text-gray-700">{translations[language].officerDesignation} *</label>
                    <div className="relative">
                      <Building className="absolute left-4 top-4 h-5 w-5 text-gray-400" />
                      <input
                        type="text"
                        required
                        value={formData.OfficerDesignation}
                        onChange={(e) => handleInputChange('OfficerDesignation', e.target.value)}
                        className="w-full pl-12 p-4 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all font-medium"
                        placeholder={translations[language].enterOfficerDesignation}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Objection Details */}
              <div className="mb-8">
                <h3 className="text-xl font-semibold text-gray-800 mb-6 flex items-center">
                  <AlertCircle className="w-5 h-5 mr-2 text-indigo-600" />
                  {translations[language].caseDetails}
                </h3>

                {/* Debug information */}
                {/* <div className="mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <h4 className="font-semibold text-yellow-800">Debug Info:</h4>
                  <p><strong>formData.ObjectionDetails:</strong> {formData.ObjectionDetails || 'EMPTY'}</p>
                  <p><strong>Length:</strong> {formData.ObjectionDetails?.length || 0}</p>
                  <p><strong>Type:</strong> {typeof formData.ObjectionDetails}</p>
                  <p><strong>objectionData exists:</strong> {objectionData ? 'YES' : 'NO'}</p>
                  {objectionData && (
                    <p><strong>objectionData.objection_details:</strong> {objectionData.objection_details || 'EMPTY'}</p>
                  )}
                </div> */}

                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-gray-700">{translations[language].objectionDetails} *</label>
                  <textarea
                    required
                    value={formData.ObjectionDetails || ''} // Add fallback empty string
                    disabled
                    onChange={(e) => handleInputChange('ObjectionDetails', e.target.value)}
                    rows="4"
                    className="w-full p-4 bg-gray-50 border border-gray-200 rounded-xl text-gray-600 cursor-not-allowed font-medium resize-none"
                    placeholder={translations[language].EnterDetailedObjectionInfo}
                  />
                </div>
              </div>

              {/* Final Decision Field - Show only when status is Completed */}
              {formData.HearingStatus === 'Completed' && (
                <div className="mb-8 bg-gradient-to-r from-green-50 to-emerald-50 rounded-2xl p-6 border border-green-200">
                  <h3 className="text-xl font-semibold text-green-800 mb-4 flex items-center">
                    <CheckCircle className="w-5 h-5 mr-2" />
                   {translations[language].finalDecision}
                  </h3>
                  <div className="space-y-2">
                    <label className="block text-sm font-semibold text-green-800">
                      {translations[language].finalDecision} <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      value={formData.final_decision || ''}
                      onChange={(e) => handleInputChange('final_decision', e.target.value)}
                      placeholder={translations[language].EnterFinalDecision}
                      className="w-full p-4 border border-green-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 min-h-[120px] font-medium"
                      required={formData.HearingStatus === 'Completed'}
                    />
                  </div>
                  <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-xl">
                    <p className="text-red-800 text-sm font-medium flex items-center">
                      <AlertCircle className="w-4 h-4 mr-2" />
                      {translations[language].finalVerdictRequiredMessage}
                    </p>
                  </div>
                </div>
              )}

              {/* Remarks */}
              <div className="mb-8">
                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-gray-700">{translations[language].remarks} *</label>
                  <textarea
                    required
                    value={formData.Remarks}
                    onChange={(e) => handleInputChange('Remarks', e.target.value)}
                    rows="3"
                    className="w-full p-4 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all resize-none font-medium"
                    placeholder={translations[language].EnterRemarks}
                  />
                </div>
              </div>

              {/* Enhanced Document Upload Section */}
              <div className="mb-8">
                <h3 className="text-xl font-semibold text-gray-800 mb-6 flex items-center">
                  <Upload className="w-5 h-5 mr-2 text-indigo-600" />
                  {translations[language].documentUpload}
                  {formData.HearingStatus === 'Completed' && (
                    <span className="ml-3 px-3 py-1 bg-red-100 text-red-800 text-xs font-medium rounded-full">
                     {translations[language].finalVerdictRequired}
                    </span>
                  )}
                </h3>

                {/* Document Upload Area */}
                <div className="border-2 border-dashed border-indigo-300 rounded-2xl p-8 bg-gradient-to-br from-indigo-50 to-blue-50 hover:from-indigo-100 hover:to-blue-100 transition-all duration-300">
                  <input
                    type="file"
                    accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                    onChange={handleDocumentUpload}
                    className="hidden"
                    id="document-upload"
                    multiple
                  />
                  <label htmlFor="document-upload" className="cursor-pointer flex flex-col items-center justify-center">
                    <div className="w-20 h-20 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-full flex items-center justify-center mb-4 shadow-lg">
                      <Upload className="h-10 w-10 text-white" />
                    </div>
                    <h4 className="text-xl font-semibold text-gray-700 mb-2">{translations[language].uploadDocuments}</h4>
                    <p className="text-sm text-gray-600 text-center mb-2">
                      {translations[language].clickToUpload}
                    </p>
                    <p className="text-xs text-gray-500">{translations[language].maxFileSize}</p>
                  </label>
                </div>

                {/* Uploaded Documents List */}
                {uploadedDocuments.length > 0 && (
                  <div className="mt-8">
                    <h4 className="text-lg font-semibold text-gray-800 mb-4">{translations[language].uploadDocuments}</h4>
                    <div className="space-y-4">
                      {uploadedDocuments.map((doc, index) => (
                        <div key={index} className="flex items-center justify-between p-4 bg-gradient-to-r from-gray-50 to-gray-100 rounded-2xl border border-gray-200 shadow-sm">
                          <div className="flex items-center flex-1">
                            <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center mr-4 shadow-md">
                              {doc.file.type.includes('pdf') ? (
                                <FileText className="h-6 w-6 text-white" />
                              ) : doc.file.type.includes('image') ? (
                                <Image className="h-6 w-6 text-white" />
                              ) : (
                                <FileText className="h-6 w-6 text-white" />
                              )}
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center space-x-3 mb-2">
                                <span className="text-sm font-semibold text-gray-800">{doc.file.name}</span>
                                <span className="text-xs text-gray-500 bg-white px-2 py-1 rounded-full">
                                  {(doc.file.size / 1024 / 1024).toFixed(2)} MB
                                </span>
                              </div>
                              <div className="flex items-center space-x-3">
                                <input
                                  type="text"
                                  placeholder={translations[language].EnterDocumentName}
                                  value={doc.customName}
                                  onChange={(e) => handleDocumentNameChange(index, e.target.value)}
                                  className="flex-1 p-3 text-sm border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 font-medium"
                                />
                                {formData.HearingStatus === 'Completed' && (
                                  <label className="flex items-center space-x-2 bg-white px-3 py-2 rounded-xl border border-gray-200">
                                    <input
                                      type="checkbox"
                                      checked={doc.isFinalVerdict || false}
                                      onChange={(e) => handleFinalVerdictToggle(index, e.target.checked)}
                                      className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                                    />
                                    <span className="text-sm font-medium text-gray-700">{translations[language].finalVerdict}</span>
                                  </label>
                                )}
                              </div>
                            </div>
                          </div>
                          <button
                            onClick={() => removeDocument(index)}
                            className="ml-4 p-2 text-red-500 hover:bg-red-50 rounded-xl transition-colors"
                          >
                            <X className="h-5 w-5" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Upload Progress */}
                {uploadProgress > 0 && uploadProgress < 100 && (
                  <div className="mt-6">
                    <div className="flex items-center justify-between text-sm text-gray-600 mb-3">
                      <span className="font-medium">{translations[language].uploadingDocuments}</span>
                      <span className="font-bold">{uploadProgress}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-3">
                      <div
                        className="bg-gradient-to-r from-indigo-500 to-purple-600 h-3 rounded-full transition-all duration-300"
                        style={{ width: `${uploadProgress}%` }}
                      ></div>
                    </div>
                  </div>
                )}

                {fileUploaded && (
                  <div className="mt-6 p-4 bg-gradient-to-r from-green-50 to-emerald-50 text-green-800 rounded-2xl flex items-center border border-green-200">
                    <CheckCircle size={20} className="mr-3" />
                    <span className="font-medium">{translations[language].documentsUploadedSuccess}</span>
                  </div>
                )}
              </div>

              {/* Enhanced Action Buttons */}
              <div className="border-t border-gray-200 pt-8">
                <div className="flex flex-wrap gap-4">
                  {formData.HearingStatus === 'Completed' ? (
                    <>
                      {/* ✅ Submit button */}
                      <button
                        type="submit"
                        disabled={isSubmitting}
                        className="px-8 py-4 bg-gradient-to-r from-green-600 to-emerald-700 text-white rounded-2xl hover:from-green-700 hover:to-emerald-800 disabled:opacity-50 disabled:cursor-not-allowed flex items-center font-semibold transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                      >
                        {isSubmitting ? (
                          <>
                            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-3"></div>
                            {translations[language].submitting}
                          </>
                        ) : (
                          <>
                            <CheckCircle className="h-5 w-5 mr-3" />
                            {translations[language].submit}
                          </>
                        )}
                      </button>

                      {/* ✅ Share button
                      <button
                        type="button"
                        onClick={() => (true)}
                        disabled={!activeHearing}setShareModalOpen
                        className={`px-8 py-4 flex items-center font-semibold rounded-2xl transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 ${!activeHearing
                          ? 'bg-gray-300 cursor-not-allowed text-gray-500'
                          : 'bg-gradient-to-r from-purple-600 to-pink-700 hover:from-purple-700 hover:to-pink-800 text-white'
                          }`}
                      >
                        <Share2 size={18} className="mr-3" />
                        Share
                      </button> */}
                    </>
                  ) : formData.HearingStatus === 'Scheduled' ? (
                    <>
                      {/* ✅ Schedule button */}
                      <button
                        type="submit"
                        disabled={isSubmitting}
                        className="px-8 py-4 bg-gradient-to-r from-indigo-600 to-purple-700 text-white rounded-2xl hover:from-indigo-700 hover:to-purple-800 disabled:opacity-50 disabled:cursor-not-allowed flex items-center font-semibold transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                      >
                        {isSubmitting ? (
                          <>
                            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-3"></div>
                            {translations[language].scheduling}
                          </>
                        ) : (
                          <>
                            <Calendar className="h-5 w-5 mr-3" />
                            {translations[language].scheduleHearing}
                          </>
                        )}
                      </button>

                      {/* ✅ Generate Notice button */}
                      <button
                        type="button"
                        onClick={handleGenerateNotice}
                        disabled={!canGenerateNotice()}
                        className={`px-8 py-4 flex items-center font-semibold rounded-2xl transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 ${!canGenerateNotice()
                          ? 'bg-gray-300 cursor-not-allowed text-gray-500'
                          : 'bg-gradient-to-r from-green-600 to-emerald-700 hover:from-green-700 hover:to-emerald-800 text-white'
                          }`}
                      >
                        <FileText size={18} className="mr-3" />
                        {translations[language].generateNotice}
                      </button>

                      {/* ✅ Share button */}
                      {/* <button
                        type="button"
                        onClick={() => setShareModalOpen(true)}
                        disabled={!activeHearing}
                        className={`px-8 py-4 flex items-center font-semibold rounded-2xl transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 ${!activeHearing
                          ? 'bg-gray-300 cursor-not-allowed text-gray-500'
                          : 'bg-gradient-to-r from-purple-600 to-pink-700 hover:from-purple-700 hover:to-pink-800 text-white'
                          }`}
                      >
                        <Share2 size={18} className="mr-3" />
                        Share
                      </button> */}

                      {/* ✅ Reset button */}
                      <button
                        type="button"
                        onClick={resetForm}
                        className="px-8 py-4 bg-gradient-to-r from-gray-500 to-gray-600 text-white rounded-2xl hover:from-gray-600 hover:to-gray-700 flex items-center font-semibold transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                      >
                        <X className="h-5 w-5 mr-3" />
                        {translations[language].resetForm}
                      </button>
                    </>
                  ) : null}
                </div>

              </div>
            </div>
          </form>
        )}
      </div>

      {/* Notice Modal */}
      {showNoticeModal && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center p-4 z-50 backdrop-blur-sm">
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-4xl relative border border-gray-200">
            <div className="p-8">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-2xl font-bold text-gray-800">{translations[language].hearingNotice}</h3>
                <button onClick={() => setShowNoticeModal(false)} className="text-gray-400 hover:text-gray-600 p-2 rounded-full hover:bg-gray-100 transition-colors">
                  <X size={24} />
                </button>
              </div>

              <div className="bg-gray-50 rounded-2xl p-6 max-h-[60vh] overflow-y-auto border">
                <Section11Notice formData={formData} objectionData={normalizeObjectionData(objectionData)} />
              </div>

              <div className="mt-6 flex space-x-3 justify-end">
                <button
                  // onClick={() => window.print()}
                  onClick={handleDownloadNotice}
                  className="bg-indigo-600 text-white px-4 py-2 rounded hover:bg-indigo-700 flex items-center"
                >
                  <Download size={18} className="mr-2" />
                  {translations[language].printNotice}
                </button>

                {/*<button
                  onClick={handleSubmitHearing}
                  className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 flex items-center"
                >
                  <Calendar size={18} className="mr-2" />
                  Schedule Hearing
                </button>*/}

                <button
                  onClick={() => setShowNoticeModal(false)}
                  className="border border-gray-300 text-gray-700 px-4 py-2 rounded hover:bg-gray-50"
                >
                  {translations[language].close}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

     {/* Share Modal */}
{shareModalOpen && (
  <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center p-4 z-50 backdrop-blur-sm">
    <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md border border-gray-200">
      <div className="p-8">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-2xl font-bold text-gray-800">{translations[language].ShareHearingDetails}</h3>
          <button
            onClick={() => setShareModalOpen(false)}
            className="text-gray-400 hover:text-gray-600 p-2 rounded-full hover:bg-gray-100 transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleShare}>
          <div className="space-y-6">

            <div className="space-y-2">
              <label className="block text-sm font-semibold text-gray-700">{translations[language].Email}</label>
              <div className="relative">
                <Mail size={20} className="absolute left-4 top-4 text-gray-400" />
                <input
                  type="email"
                  value={shareEmail}
                  readOnly
                  disabled
                  onChange={e => setShareEmail(e.target.value)}
                  className="w-full pl-12 p-4 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 font-medium"
                />
              </div>
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-semibold text-gray-700">{translations[language].MobileNumber}</label>
              <div className="relative">
                <Bell size={20} className="absolute left-4 top-4 text-gray-400" />
                <input
                  type="tel"
                  value={sharePhone}
                  readOnly
                  disabled
                  onChange={e => setSharePhone(e.target.value)}
                  className="w-full pl-12 p-4 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 font-medium"
                />
              </div>
            </div>

            {/* Conditional Upload Field */}
            {shareModalSource === "completed" ? (
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-gray-700">
                  {translations[language].uploadFinalVerdictPDF}*
                </label>
                <input
                  type="file"
                  accept="application/pdf"
                  required
                  onChange={(e) =>
                    setFiles((prev) => ({ ...prev, finalVerdict: e.target.files[0] }))
                  }
                  className="w-full p-3 border border-gray-300 rounded-xl bg-white"
                />
              </div>
            ) : (
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-gray-700">
                  {translations[language].uploadHearingNoticePDF}*
                </label>
                <input
                  type="file"
                  accept="application/pdf"
                  required
                  onChange={(e) =>
                    setFiles(prev => ({ ...prev, manualNotice: e.target.files[0] }))
                  }
                  className="w-full p-3 border border-gray-300 rounded-xl bg-white"
                />
              </div>
            )}

            <div className="pt-4">
              <button
                type="submit"
                disabled={isSendingNotice || (!shareEmail && !sharePhone)}
                className={`w-full py-4 px-6 rounded-2xl flex items-center justify-center font-semibold transition-all duration-300 shadow-lg hover:shadow-xl
                  ${!shareEmail && !sharePhone
                    ? 'bg-gray-300 cursor-not-allowed text-gray-500'
                    : isSendingNotice
                      ? 'bg-gray-400 cursor-wait text-white'
                      : 'bg-gradient-to-r from-purple-600 to-pink-700 hover:from-purple-700 hover:to-pink-800 text-white transform hover:-translate-y-0.5'
                  }`}
              >
                {isSendingNotice ? (
                  <>
                    <svg
                      className="animate-spin mr-2 h-5 w-5 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8v8z"
                      ></path>
                    </svg>
                    {translations[language].sending}
                  </>
                ) : (
                  <>
                    <Share2 size={18} className="mr-2" />
                    {shareModalSource === "completed"
                      ? "Submit Final Verdict"
                      : translations[language].sendnotification}
                  </>
                )}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  </div>
)}

      {/* View Hearing Modal - FIXED VERSION WITH ENHANCED DESIGN */}
      {showViewModal && viewHearing && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center p-4 z-50 backdrop-blur-sm">
          {/* Modal Container */}
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-7xl max-h-[95vh] flex flex-col border border-gray-200">

            {/* Fixed Header */}
            <div className="sticky top-0 bg-gradient-to-r from-blue-600 to-blue-900 text-white p-6 flex justify-between items-center rounded-t-3xl flex-shrink-0">
              <div>
                <h3 className="text-2xl font-bold">{translations[language].objectionCaseDetails}</h3>
                <p className="text-white-200">{translations[language].caseNumber}:
                  {objectionData?.case_no || `LAQ${formData?.objection_date
                    ? new Date(formData.objection_date).getFullYear()
                    : new Date().getFullYear()}${formData.objection_type === 'Private' ? 'P' : 'G'}${formData.objection_id || ''}`}
                </p>
              </div>
              <button onClick={() => setShowViewModal(false)} className="text-white hover:text-gray-200 p-2 rounded-full hover:bg-white hover:bg-opacity-20 transition-colors">
                <X size={24} />
              </button>
            </div>

            {/* Scrollable Body */}
            <div className="p-8 overflow-y-auto flex-1">
              {/* Top Row: Objection Details & Objector Details */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
                {/* Objection Details - Left Side */}
                <div className="bg-gradient-to-br from-blue-50 to-indigo-100 rounded-2xl p-6 border border-blue-200">
                  <h4 className="text-xl font-bold mb-6 text-indigo-900 flex items-center">
                    <FileText className="w-6 h-6 mr-3" />
                   {translations[language].objectionInformation}
                  </h4>
                  <div className="space-y-4">
                    {viewHearing.objection?.objection_date && (
                      <div className="flex items-center p-3 bg-white rounded-xl shadow-sm">
                        <Calendar size={20} className="text-indigo-600 mr-3" />
                        <div>
                          <span className="text-gray-600 font-medium text-sm">{translations[language].objectionDate}</span>
                          <p className="font-semibold text-gray-800">{format(new Date(viewHearing.objection.objection_date), 'dd-MM-yyyy')}</p>
                        </div>
                      </div>
                    )}
                    {viewHearing.objection?.objection_id && (
                      <div className="flex items-center p-3 bg-white rounded-xl shadow-sm">
                        <Tag size={20} className="text-indigo-600 mr-3" />
                        <div>
                          <span className="text-gray-600 font-medium text-sm">{translations[language].caseNumber}</span>
                          <p className="font-semibold text-gray-800"> {objectionData?.case_no || `LAQ${formData?.objection_date
                            ? new Date(formData.objection_date).getFullYear()
                            : new Date().getFullYear()}${formData.objection_type === 'Private' ? 'P' : 'G'}${formData.objection_id || ''}`}</p>
                        </div>
                      </div>
                    )}
                    {(viewHearing.objection?.affected_survey_no || viewHearing.objection?.eng_village_name || viewHearing.objection?.eng_tehsil_name) && (
                      <div className="flex items-center p-3 bg-white rounded-xl shadow-sm">
                        <MapPin size={20} className="text-indigo-600 mr-3" />
                        <div>
                          <span className="text-gray-600 font-medium text-sm">{translations[language].affectedArea}</span>
                          <p className="font-semibold text-gray-800">
                            {[
                              viewHearing.objection?.affected_survey_no ? `Survey No. ${viewHearing.objection.affected_survey_no}` : '',
                              viewHearing.objection?.eng_village_name || '',
                              viewHearing.objection?.eng_tehsil_name || ''
                            ].filter(Boolean).join(', ')}
                          </p>
                        </div>
                      </div>
                    )}
                    {viewHearing.objection?.objection_category && (
                      <div className="flex items-start p-3 bg-white rounded-xl shadow-sm">
                        <Tag size={20} className="text-indigo-600 mr-3 mt-1" />
                        <div>
                          <span className="text-gray-600 font-medium text-sm">{translations[language].category}</span>
                          <p className="font-semibold text-gray-800">{viewHearing.objection.objection_category}</p>
                        </div>
                      </div>
                    )}
                    {/* {viewHearing.objection?.objection_summary && (
                      <div className="flex items-start p-3 bg-white rounded-xl shadow-sm">
                        <AlertCircle size={20} className="text-indigo-600 mr-3 mt-1" />
                        <div>
                          <span className="text-gray-600 font-medium text-sm">Summary</span>
                          <p className="text-gray-800">{viewHearing.objection.objection_summary}</p>
                        </div>
                      </div>
                    )}
                    {viewHearing.objection?.objection_details && (
                      <div className="flex items-start p-3 bg-white rounded-xl shadow-sm">
                        <FileText size={20} className="text-indigo-600 mr-3 mt-1" />
                        <div>
                          <span className="text-gray-600 font-medium text-sm">Details</span>
                          <p className="text-gray-800 text-sm">{viewHearing.objection.objection_details}</p>
                        </div>
                      </div>
                    )} */}
                    {/* ✅ Objection Summary */}
                    {viewHearing?.objection?.objection_summary && (
                      <div className="flex items-start p-3 bg-white rounded-xl shadow-sm">
                        <FileText size={20} className="text-indigo-600 mr-3 mt-1" />
                        <div>
                          <span className="text-gray-600 font-medium text-sm">{translations[language].objectionSummary}</span>
                          <p className="font-semibold text-gray-800 whitespace-pre-line mt-1">
                            {viewHearing.objection.objection_summary}
                          </p>
                        </div>
                      </div>
                    )}
                    {/* ✅ Objection Details */}
                    {viewHearing?.objection?.objection_details && (
                      <div className="flex items-start p-3 bg-white rounded-xl shadow-sm">
                        <FileText size={20} className="text-indigo-600 mr-3 mt-1" />
                        <div>
                          <span className="text-gray-600 font-medium text-sm">{translations[language].objectionSummary}</span>
                          <p className="font-semibold text-gray-800 whitespace-pre-line mt-1">
                            {viewHearing.objection.objection_details}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Objector Details - Right Side */}
                <div className="bg-gradient-to-br from-emerald-50 to-green-100 rounded-2xl p-6 border border-emerald-200">
                  <h4 className="text-xl font-bold mb-6 text-emerald-900 flex items-center">
                    <User className="w-6 h-6 mr-3" />
                    {translations[language].objectorInformation}
                  </h4>
                  <div className="space-y-4">
                    {/* {viewHearing.objection?.objector_name && ( */}
                    <div className="flex items-center p-3 bg-white rounded-xl shadow-sm">
                      <User size={20} className="text-emerald-600 mr-3" />
                      <div>
                        <span className="text-gray-600 font-medium text-sm">{translations[language].name}</span>
                        <p className="font-semibold text-gray-800">{displayObjection.name}</p>
                      </div>
                    </div>

                    {/* {viewHearing.objection?.objector_contact && ( */}
                    <div className="flex items-center p-3 bg-white rounded-xl shadow-sm">
                      <Phone size={20} className="text-emerald-600 mr-3" />
                      <div>
                        <span className="text-gray-600 font-medium text-sm">{translations[language].contact}</span>
                        <p className="font-semibold text-gray-800">{displayObjection.contact}</p>
                      </div>
                    </div>

                    {/* {viewHearing.objection?.objector_email && ( */}
                    <div className="flex items-center p-3 bg-white rounded-xl shadow-sm">
                      <Mail size={20} className="text-emerald-600 mr-3" />
                      <div>
                        <span className="text-gray-600 font-medium text-sm">{translations[language].email}</span>
                        <p className="text-gray-800">{displayObjection.email}</p>
                      </div>
                    </div>

                    {/* {viewHearing.objection?.objector_address && ( */}
                    <div className="flex items-start p-3 bg-white rounded-xl shadow-sm">
                      <MapPin size={20} className="text-emerald-600 mr-3 mt-1" />
                      <div>
                        <span className="text-gray-600 font-medium text-sm">{translations[language].address}</span>
                        <p className="text-gray-800">{displayObjection.address}</p>
                      </div>
                    </div>

                  </div>
                </div>
              </div>

              {/* Hearing Controls Row */}
              <div className="bg-gradient-to-r from-amber-50 to-orange-100 rounded-2xl p-6 mb-8 border border-amber-200">
                <h4 className="text-xl font-bold mb-6 text-amber-900 flex items-center">
                  <Clock className="w-6 h-6 mr-3" />
                 {translations[language].hearingStatusManagement}
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">{translations[language].hearingStatus}</label>
                    {isEditing ? (
                      <select
                        value={editingHearing?.HearingStatus || ''}
                        onChange={(e) => handleEditingChange('HearingStatus', e.target.value)}
                        className="w-full border border-gray-300 rounded-xl px-4 py-3 focus:ring-indigo-500 focus:border-indigo-500 font-medium"
                      >
                        <option value="Scheduled">{translations[language].statusScheduled}</option>
                        <option value="Completed">{translations[language].statusCompleted}</option>
                      </select>
                    ) : (
                      <div className="p-4 bg-white rounded-xl shadow-sm">
                        {getStatusBadge(viewHearing.hearing?.[0]?.HearingStatus)}
                      </div>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">{translations[language].hearingdate}</label>
                    {isEditing ? (
                      <input
                        type="date"
                        value={editingHearing?.HearingDate ? new Date(editingHearing.HearingDate).toISOString().split('T')[0] : ''}
                        onChange={(e) => handleEditingChange('HearingDate', e.target.value)}
                        className="w-full border border-gray-300 rounded-xl px-4 py-3 focus:ring-indigo-500 focus:border-indigo-500 font-medium"
                      />
                    ) : (
                      <div className="p-4 bg-white rounded-xl shadow-sm flex items-center">
                        <Calendar size={16} className="text-gray-500 mr-2" />
                        {/* <span className="font-medium">{new Date(viewHearing.hearing?.[0]?.HearingDate).toLocaleDateString()}</span> */}
                        <span className="font-medium">
                          {viewHearing.hearing?.[0]?.HearingDate
                            ? format(new Date(viewHearing.hearing[viewHearing.hearing.length - 1].HearingDate), "dd-MM-yyyy")
                            : "N/A"}
                        </span>
                      </div>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">{translations[language].hearingTime}</label>
                    {isEditing ? (
                      <input
                        type="time"
                        required
                        value={formData.HearingTime}
                        onChange={(e) => handleInputChange('HearingTime', e.target.value)}
                        className="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all font-medium"
                      />
                    ) : (
                      <div className="p-4 bg-white rounded-xl shadow-sm flex items-center">
                        <Clock size={16} className="text-gray-500 mr-2" />
                        <span className="font-medium">{viewHearing.hearing?.length
                          ? viewHearing.hearing[viewHearing.hearing.length - 1].HearingTime?.substring(11, 16)
                          : "N/A"}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Final Decision Field - Show only when status is Completed */}
              {(isEditing && editingHearing?.HearingStatus === 'Completed') ||
                (!isEditing && viewHearing.hearing?.[0]?.HearingStatus === 'Completed') ? (
                <div className="mb-8 bg-gradient-to-r from-green-50 to-emerald-50 rounded-2xl p-6 border border-green-200">
                  <h4 className="text-xl font-bold mb-4 text-green-900 flex items-center">
                    <CheckCircle className="w-6 h-6 mr-3" />
                    {translations[language].finalDecision}
                  </h4>
                  {isEditing ? (
                    <div className="space-y-2">
                      <label className="block text-sm font-semibold text-green-800">
                        {translations[language].finalDecision} <span className="text-red-500">*</span>
                      </label>
                      <textarea
                        value={editingHearing?.final_decision || ''}
                        onChange={(e) => handleEditingChange('final_decision', e.target.value)}
                        placeholder={translations[language].EnterFinalDecision}
                        className="w-full border border-green-300 rounded-xl px-4 py-3 focus:ring-green-500 focus:border-green-500 min-h-[100px] font-medium"
                        required
                      />
                    </div>
                  ) : (
                    <div className="bg-white rounded-xl p-4 border shadow-sm">
                      <p className="text-gray-800">{viewHearing.hearing?.[0]?.final_decision || 'No final decision recorded'}</p>
                    </div>
                  )}
                </div>
              ) : null}

              {/* Hearing Progress Section */}
              <div className="mb-8">
                <h4 className="text-xl font-bold mb-6 text-gray-800 flex items-center">
                  <CheckCircle className="w-6 h-6 mr-3 text-green-600" />
                  {translations[language].objectionHearingProgress}
                </h4>

                {/* Hearing History Section */}
                <div className="mb-6">
                  <div className="space-y-4">
                    <div className="relative pl-6 border-l-2 border-green-200">
                      {viewHearing.hearing?.map((hearing, index) => (
                        <div key={hearing.HearingID} className="mb-4 relative">
                          <div className="absolute -left-[13px] top-0 w-6 h-6 rounded-full bg-green-100 border-2 border-green-600 flex items-center justify-center">
                            <span className="text-xs font-bold text-green-600">{index + 1}</span>
                          </div>
                          <div className="ml-4">
                            <div className="bg-white border rounded-xl p-4 shadow-sm">
                              <div className="flex justify-between items-start mb-3">
                                <div className="flex items-center space-x-4">
                                  <div className="flex items-center">
                                    <Calendar size={16} className="text-gray-500 mr-2" />
                                    <span className="text-sm font-medium text-gray-900">
                                      {format(new Date(hearing.HearingDate), 'dd-MM-yyyy')}
                                    </span>
                                  </div>
                                  <div className="flex items-center">
                                    <User size={16} className="text-gray-500 mr-2" />
                                    <span className="text-sm font-medium text-gray-900">
                                      {hearing.OfficerName}
                                    </span>
                                  </div>
                                </div>
                                <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                                  {hearing.OfficerDesignation}
                                </span>
                              </div>
                              <div className="mt-2">
                                <span className="text-xs font-medium text-gray-600 uppercase tracking-wide">{translations[language].Remarks}:</span>
                                <p className="text-sm text-gray-700 mt-1">
                                  {hearing.Remarks && hearing.Remarks.trim() !== ''
                                    ? hearing.Remarks
                                    : 'No remarks for this hearing'}
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Completed Status Content */}
                {viewHearing.hearing?.[0]?.HearingStatus === "Completed" && (
                  <div className="space-y-4">
                    {/* Final Decision Display */}
                    {viewHearing.hearing?.[0]?.final_decision && (
                      <div className="bg-green-50 border border-green-200 rounded-xl p-4">
                        <h5 className="font-medium text-green-800 mb-2">{translations[language].finalDecision}:</h5>
                        <p className="text-gray-800">{viewHearing.hearing?.[0].final_decision}</p>
                      </div>
                    )}

                    {viewHearing.objection?.resolution_details && (
                      <div className="bg-green-50 border border-green-200 rounded-xl p-4">
                        <h5 className="font-medium text-green-800 mb-2">{translations[language].FinalResolution}:</h5>
                        <p className="text-gray-800">{viewHearing.objection.resolution_details}</p>
                        {viewHearing.objection?.resolution_date && (
                          <p className="text-sm text-gray-600 mt-2">
                            {translations[language].resolvedOn}: {new Date(viewHearing.objection.resolution_date).toLocaleDateString()}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Attached Documents Section */}
              <div className="mb-8">
                <h4 className="text-xl font-bold mb-6 text-gray-800 flex items-center">
                  <Paperclip className="w-6 h-6 mr-3 text-gray-600" />
                  {translations[language].attachedDocuments}
                </h4>
                {viewHearing.documents && viewHearing.documents.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {viewHearing.documents.map((doc, index) => (
                      <div key={index} className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-4 flex items-center justify-between shadow-sm">
                        <div className="flex items-center">
                          <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center mr-3">
                            <FileText size={20} className="text-white" />
                          </div>
                          <div>
                            <span className="font-medium text-gray-800">{doc.document_name}</span>
                            {doc.document_name === 'Final Verdict' && (
                              <span className="block text-xs text-green-600 font-medium">VERDICT</span>
                            )}
                          </div>
                        </div>
                        <button
                          onClick={() => window.open(doc.upload_path, '_blank')}
                          className="text-blue-600 hover:text-blue-800 p-2 rounded-lg hover:bg-blue-100 transition-colors"
                        >
                          <Download size={16} />
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="bg-gray-50 rounded-xl p-6 text-center">
                    <FileText className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                    <p className="text-gray-500">{translations[language].noDocumentsAttached}</p>
                  </div>
                )}

                {/* Show Final Verdict requirement if completing hearing */}
                {isEditing && editingHearing?.HearingStatus === 'Completed' && (
                  <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-xl">
                    <p className="text-red-800 text-sm font-medium flex items-center">
                      <AlertCircle className="w-4 h-4 mr-2" />
                     {translations[language].finalVerdictRequiredMessage}
                    </p>
                  </div>
                )}
              </div>

              {/* Document Upload Section (if editing) */}
              {isEditing && (
                <div className="mb-8">
                  <h4 className="text-xl font-bold mb-6 text-gray-800 flex items-center">
                    <Upload className="w-6 h-6 mr-3 text-indigo-600" />
                    {translations[language].uploadAdditionalDocuments}
                    {editingHearing?.HearingStatus === 'Completed' && (
                      <span className="ml-3 px-3 py-1 bg-red-100 text-red-800 text-xs font-medium rounded-full">
                        {translations[language].finalVerdictRequired}
                      </span>
                    )}
                  </h4>

                  {/* Document Upload Area */}
                  <div className="border-2 border-dashed border-indigo-300 rounded-2xl p-6 bg-gradient-to-br from-indigo-50 to-blue-50 hover:from-indigo-100 hover:to-blue-100 transition-all duration-300">
                    <input
                      type="file"
                      accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                      onChange={handleDocumentUpload}
                      className="hidden"
                      id="modal-document-upload"
                      multiple
                    />
                    <label htmlFor="modal-document-upload" className="cursor-pointer flex flex-col items-center justify-center">
                      <div className="w-16 h-16 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-full flex items-center justify-center mb-4 shadow-lg">
                        <Upload className="h-8 w-8 text-white" />
                      </div>
                      <h5 className="text-lg font-semibold text-gray-700 mb-2">{translations[language].uploadedDocuments}</h5>
                      <p className="text-sm text-gray-600 text-center mb-2">
                        {translations[language].clickToUpload}
                      </p>
                      <p className="text-xs text-gray-500">{translations[language].maxFileSize}</p>
                    </label>
                  </div>

                  {/* Uploaded Documents List */}
                  {uploadedDocuments.length > 0 && (
                    <div className="mt-6">
                      <h5 className="text-lg font-semibold text-gray-800 mb-4">{translations[language].recentlyUploadedDocuments}</h5>
                      <div className="space-y-3">
                        {uploadedDocuments.map((doc, index) => (
                          <div key={index} className="flex items-center justify-between p-4 bg-gradient-to-r from-gray-50 to-gray-100 rounded-xl border border-gray-200 shadow-sm">
                            <div className="flex items-center flex-1">
                              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mr-3">
                                {doc.file.type.includes('pdf') ? (
                                  <FileText className="h-5 w-5 text-blue-600" />
                                ) : doc.file.type.includes('image') ? (
                                  <Image className="h-5 w-5 text-green-600" />
                                ) : (
                                  <FileText className="h-5 w-5 text-gray-600" />
                                )}
                              </div>
                              <div className="flex-1">
                                <div className="flex items-center space-x-3">
                                  <span className="text-sm font-medium text-gray-700">{doc.file.name}</span>
                                  <span className="text-xs text-gray-500">
                                    ({(doc.file.size / 1024 / 1024).toFixed(2)} MB)
                                  </span>
                                </div>
                                <div className="mt-2 flex items-center space-x-3">
                                  <input
                                    type="text"
                                    placeholder={translations[language].EnterDocumentDescription}
                                    value={doc.customName}
                                    onChange={(e) => handleDocumentNameChange(index, e.target.value)}
                                    className="flex-1 p-2 text-sm border border-gray-300 rounded-lg focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                                  />
                                  {isEditing && editingHearing?.HearingStatus === 'Completed' && (
                                    <label className="flex items-center space-x-2">
                                      <input
                                        type="checkbox"
                                        checked={doc.isFinalVerdict || false}
                                        onChange={(e) => handleFinalVerdictToggle(index, e.target.checked)}
                                        className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                                      />
                                      <span className="text-sm text-gray-700">{translations[language].finalVerdict}</span>
                                    </label>
                                  )}
                                </div>
                              </div>
                            </div>
                            <button
                              onClick={() => removeDocument(index)}
                              className="ml-3 p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                            >
                              <X className="h-4 w-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Upload Progress */}
                  {uploadProgress > 0 && uploadProgress < 100 && (
                    <div className="mt-4">
                      <div className="flex items-center justify-between text-sm text-gray-600 mb-2">
                        <span>{translations[language].uploadingDocuments}</span>
                        <span>{uploadProgress}%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-gradient-to-r from-indigo-500 to-purple-600 h-2 rounded-full transition-all duration-300"
                          style={{ width: `${uploadProgress}%` }}
                        ></div>
                      </div>
                    </div>
                  )}

                  {fileUploaded && (
                    <div className="mt-4 p-3 bg-gradient-to-r from-green-50 to-emerald-50 text-green-800 rounded-xl flex items-center border border-green-200">
                      <CheckCircle size={16} className="mr-2" />
                      {translations[language].documentsUploadedSuccess}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Fixed Footer */}
            <div className="flex justify-end space-x-4 p-6 border-t bg-gray-50 rounded-b-3xl flex-shrink-0">
              {viewHearing.hearing?.[0]?.HearingStatus !== "Completed" && (
                <>
                  {!isEditing ? (
                    <button
                      onClick={() => {
                        setShowViewModal(false);
                        setActiveTab("new");
                      }}
                      className="bg-gradient-to-r from-indigo-600 to-purple-700 text-white px-6 py-3 rounded-2xl hover:from-indigo-700 hover:to-purple-800 flex items-center font-semibold transition-all duration-300 shadow-lg hover:shadow-xl"
                    >
                      <Edit size={18} className="mr-2" />
                      {translations[language].scheduleNewHearing}
                    </button>
                  ) : (
                    <>
                      <button
                        onClick={handleUpdateStatus}
                        disabled={isSubmitting}
                        className="bg-gradient-to-r from-green-600 to-emerald-700 text-white px-6 py-3 rounded-2xl hover:from-green-700 hover:to-emerald-800 disabled:opacity-50 flex items-center font-semibold transition-all duration-300 shadow-lg hover:shadow-xl"
                      >
                        {isSubmitting ? <span>{translations[language].Updating}</span> : <span>{translations[language].saveChanges}</span>}
                      </button>
                      <button
                        onClick={() => {
                          setIsEditing(false);
                          setEditingHearing({ ...viewHearing.hearing?.[0] });
                        }}
                        className="bg-gradient-to-r from-gray-500 to-gray-600 text-white px-6 py-3 rounded-2xl hover:from-gray-600 hover:to-gray-700 font-semibold transition-all duration-300"
                      >
                        {translations[language].cancel}
                      </button>
                    </>
                  )}
                </>
              )}
              <button
                onClick={() => {
                  setShareModalOpen(true);
                  setShareModalSource("completed");
                  setShowViewModal(false);
                }}
                className="bg-gradient-to-r from-purple-600 to-pink-700 text-white flex items-center px-6 py-3 rounded-2xl hover:from-purple-700 hover:to-pink-800 font-semibold transition-all duration-300 shadow-lg hover:shadow-xl"
              >
                <Share2 size={18} className="mr-2" />
                {translations[language].shareDetails}
              </button>
              <button
                onClick={() => {
                  setShowViewModal(false);
                  setIsEditing(false);
                  setEditingHearing(null);
                }}
                className="border border-gray-300 text-gray-700 px-6 py-3 rounded-2xl hover:bg-gray-100 font-semibold transition-all duration-300"
              >
                {translations[language].close}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Enhanced View Completed Hearing Modal */}
      {showViewCompletedModal && viewHearing && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center p-4 z-50 backdrop-blur-sm">
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-7xl max-h-[95vh] overflow-y-auto border border-gray-200">

            {/* Header */}
            <div className="sticky top-0 bg-gradient-to-r from-blue-600 to-blue-900 text-white p-6 flex justify-between items-center rounded-t-3xl  z-50">
              <div>
                <h3 className="text-2xl font-bold">{translations[language].completedHearingDetails}</h3>
                <p className="text-white-200">{translations[language].caseNumber}: {objectionData?.case_no || `LAQ${formData?.objection_date
                  ? new Date(formData.objection_date).getFullYear()
                  : new Date().getFullYear()}${formData.objection_type === 'Private' ? 'P' : 'G'}${formData.objection_id || ''}`}</p>
              </div>
              <button
                onClick={() => setShowViewCompletedModal(false)}
                className="text-white hover:text-gray-200 p-2 rounded-full hover:bg-white hover:bg-opacity-20 transition-colors"
              >
                <X size={24} />
              </button>
            </div>

            <div className="p-8">
              {/* Case Information Grid */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
                {/* Case Details */}
                <div className="bg-gradient-to-br from-blue-00 to-blue-900 rounded-2xl p-6 border border-blue-200">
                  <h4 className="text-xl font-bold mb-6 text-blue-600 flex items-center">
                    <FileText className="w-6 h-6 mr-3" />
                    {translations[language].caseInformation}
                  </h4>
                  <div className="space-y-4">
                    <div className="flex items-center p-3 bg-white rounded-xl shadow-sm">
                      <Calendar size={20} className="text-indigo-600 mr-3" />
                      <div>
                        <span className="text-gray-600 font-medium text-sm">{translations[language].objectionDate}</span>
                        <p className="font-semibold text-gray-800">{objectionData?.objection_date ? format(new Date(objectionData.objection_date), 'dd-MM-yyyy') : 'N/A'}</p>
                      </div>
                    </div>
                    <div className="flex items-center p-3 bg-white rounded-xl shadow-sm">
                      <Tag size={20} className="text-indigo-600 mr-3" />
                      <div>
                        <span className="text-gray-600 font-medium text-sm">{translations[language].caseNumber}</span>
                        <p className="font-semibold text-gray-800"> {objectionData?.case_no || `LAQ${formData?.objection_date
                          ? new Date(formData.objection_date).getFullYear()
                          : new Date().getFullYear()}${formData.objection_type === 'Private' ? 'P' : 'G'}${formData.objection_id || ''}`}</p>
                      </div>
                    </div>
                    <div className="flex items-center p-3 bg-white rounded-xl shadow-sm">

                      {/* <div>
                  <span className="text-gray-600 font-medium text-sm">Affected Area</span>
                  <p className="font-semibold text-gray-800">Survey No. {objectionData?.affected_survey_no}, {objectionData?.eng_village_name}</p>
                </div> */}
                      {(viewHearing.objection?.affected_survey_no || viewHearing.objection?.eng_village_name || viewHearing.objection?.eng_tehsil_name) && (
                        <div className="flex items-center">
                          <MapPin size={18} className="text-indigo-600 mr-2" />
                          <span className="text-gray-600 font-medium mr-2">{translations[language].affectedArea}:</span>
                          <span>
                            {viewHearing.objection?.affected_survey_no && `Survey No. ${viewHearing.objection.affected_survey_no}`}
                            {viewHearing.objection?.affected_survey_no && (viewHearing.objection?.eng_village_name || viewHearing.objection?.eng_tehsil_name) && ', '}
                            {viewHearing.objection?.eng_village_name}
                            {viewHearing.objection?.eng_village_name && viewHearing.objection?.eng_tehsil_name && ', '}
                            {viewHearing.objection?.eng_tehsil_name}
                          </span>
                        </div>
                      )}
                    </div>
                    {/* ✅ Objection Summary */}
                    {viewHearing?.objection?.objection_summary && (
                      <div className="flex items-start p-3 bg-white rounded-xl shadow-sm">
                        <FileText size={20} className="text-indigo-600 mr-3 mt-1" />
                        <div>
                          <span className="text-gray-600 font-medium text-sm">{translations[language].objectionSummary}</span>
                          <p className="font-semibold text-gray-800 whitespace-pre-line mt-1">
                            {viewHearing.objection.objection_summary}
                          </p>
                        </div>
                      </div>
                    )}
                    {/* ✅ Objection Details */}
                    {viewHearing?.objection?.objection_details && (
                      <div className="flex items-start p-3 bg-white rounded-xl shadow-sm">
                        <FileText size={20} className="text-indigo-600 mr-3 mt-1" />
                        <div>
                          <span className="text-gray-600 font-medium text-sm">{translations[language].objectionDetails}</span>
                          <p className="font-semibold text-gray-800 whitespace-pre-line mt-1">
                            {viewHearing.objection.objection_details}
                          </p>
                        </div>
                      </div>
                    )}

                  </div>
                </div>

                {/* Objector Information */}
                <div className="bg-gradient-to-br from-emerald-50 to-green-100 rounded-2xl p-6 border border-emerald-200">
                  <h4 className="text-xl font-bold mb-6 text-emerald-900 flex items-center">
                    <User className="w-6 h-6 mr-3" />
                    {translations[language].objectorInformation}
                  </h4>
                  <div className="space-y-4">
                    <div className="flex items-center p-3 bg-white rounded-xl shadow-sm">
                      <User size={20} className="text-emerald-600 mr-3" />
                      <div>
                        <span className="text-gray-600 font-medium text-sm">{translations[language].name}</span>
                        <p className="font-semibold text-gray-800">{displayObjection.name}</p>
                      </div>
                    </div>
                    <div className="flex items-center p-3 bg-white rounded-xl shadow-sm">
                      <Phone size={20} className="text-emerald-600 mr-3" />
                      <div>
                        <span className="text-gray-600 font-medium text-sm">{translations[language].contact}</span>
                        <p className="font-semibold text-gray-800">{displayObjection.contact}</p>
                      </div>
                    </div>
                    <div className="flex items-center p-3 bg-white rounded-xl shadow-sm">
                      <Mail size={20} className="text-emerald-600 mr-3" />
                      <div>
                        <span className="text-gray-600 font-medium text-sm">{translations[language].email}</span>
                        <p className="text-gray-800">{displayObjection.email}</p>
                      </div>
                    </div>
                    <div className="flex items-start p-3 bg-white rounded-xl shadow-sm">
                      <MapPin size={20} className="text-emerald-600 mr-3 mt-1" />
                      <div>
                        <span className="text-gray-600 font-medium text-sm">{translations[language].address}</span>
                        <p className="text-gray-800">{displayObjection.address}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Hearing Status Row - Completed and Non-editable */}
              <div className="mb-8 bg-gradient-to-r from-green-50 to-emerald-100 rounded-2xl p-6 border border-green-200">
                <div className="flex items-center justify-center">
                  <div className="text-center">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">{translations[language].hearingStatus}</label>
                    <span className="inline-block px-6 py-3 rounded-full text-sm font-bold bg-gradient-to-r from-green-500 to-emerald-600 text-white shadow-lg">
                      {translations[language].statusCompleted}
                    </span>
                  </div>
                </div>
              </div>

              {/* Hearing History Section */}
              <div className="mb-8">
                <h4 className="text-xl font-bold mb-6 text-gray-800 flex items-center">
                  <Clock className="w-6 h-6 mr-3 text-blue-600" />
                 {translations[language].hearingHistory}
                </h4>

                <div className="space-y-4">
                  <div className="relative pl-6 border-l-2 border-green-200">
                    {viewHearing.hearing?.map((hearing, index) => (
                      <div key={hearing.HearingID} className="mb-4 relative">
                        <div className="absolute -left-[13px] top-0 w-6 h-6 rounded-full bg-green-100 border-2 border-green-600 flex items-center justify-center">
                          <span className="text-xs font-bold text-green-600">{index + 1}</span>
                        </div>
                        <div className="ml-4">
                          <div className="bg-white border rounded-xl p-4 shadow-sm">
                            <div className="flex justify-between items-start mb-3">
                              <div className="flex items-center space-x-4">
                                <div className="flex items-center">
                                  <Calendar size={16} className="text-gray-500 mr-2" />
                                  <span className="text-sm font-medium text-gray-900">
                                    {format(new Date(hearing.HearingDate), 'dd-MM-yyyy')}
                                  </span>
                                </div>
                                <div className="flex items-center">
                                  <User size={16} className="text-gray-500 mr-2" />
                                  <span className="text-sm font-medium text-gray-900">
                                    {hearing.OfficerName}
                                  </span>
                                </div>
                              </div>
                              <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                                {hearing.OfficerDesignation}
                              </span>
                            </div>
                            {hearing.Remarks && (
                              <div className="mt-2">
                                <span className="text-xs font-medium text-gray-600 uppercase tracking-wide">{translations[language].Remarks}:</span>
                                <p className="text-sm text-gray-700 mt-1">{hearing.Remarks}</p>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Final Verdict Section */}
              <div className="mb-8">
                <h4 className="text-xl font-bold mb-6 text-gray-800 flex items-center">
                  <CheckCircle className="w-6 h-6 mr-3 text-green-600" />
                  {translations[language].finalVerdict}
                </h4>

                {objectionData?.hearing_outcome ? (
                  <div className="bg-gradient-to-r from-green-50 to-emerald-100 border border-green-200 rounded-2xl p-6">
                    <div className="flex items-start">
                      <CheckCircle size={24} className="text-green-600 mr-4 mt-1 flex-shrink-0" />
                      <div className="flex-1">
                        <h5 className="font-bold text-green-800 mb-3 text-lg">{translations[language].hearingOutcome}:</h5>
                        <p className="text-gray-800 text-lg leading-relaxed mb-4">{objectionData.hearing_outcome}</p>

                        {/* Final Verdict Document */}
                        {viewHearing.documents && viewHearing.documents.some(doc =>
                          doc.document_name.toLowerCase().includes('verdict') ||
                          doc.document_name.toLowerCase().includes('final') ||
                          doc.document_name.toLowerCase().includes('outcome')
                        ) && (
                            <div className="mt-4">
                              <h6 className="text-sm font-medium text-green-700 mb-3">{translations[language].finalVerdictDocument}:</h6>
                              {viewHearing.documents
                                .filter(doc =>
                                  doc.document_name.toLowerCase().includes('verdict') ||
                                  doc.document_name.toLowerCase().includes('final') ||
                                  doc.document_name.toLowerCase().includes('outcome')
                                )
                                .map((doc, index) => (
                                  <div key={index} className="flex items-center justify-between p-3 bg-green-100 rounded-xl border border-green-300 shadow-sm">
                                    <div className="flex items-center">
                                      <div className="w-10 h-10 bg-green-600 rounded-lg flex items-center justify-center mr-3">
                                        <FileText size={20} className="text-white" />
                                      </div>
                                      <div>
                                        <span className="text-sm font-semibold text-green-800">{doc.document_name}</span>
                                        <span className="block text-xs text-green-600 font-medium">{translations[language].officialDocument}</span>
                                      </div>
                                    </div>
                                    <button
                                      onClick={() => window.open(doc.upload_path, '_blank')}
                                      className="text-green-600 hover:text-green-800 p-2 rounded-lg hover:bg-green-200 transition-colors"
                                    >
                                      <Download size={16} />
                                    </button>
                                  </div>
                                ))}
                            </div>
                          )}

                        {objectionData?.resolution_date && (
                          <div className="mt-4 flex items-center text-sm text-gray-600 bg-green-50 rounded-lg p-3 border border-green-200">
                            <Calendar size={14} className="mr-2" />
                            <span className="font-medium">{translations[language].resolvedOn}: {new Date(objectionData.resolution_date).toLocaleDateString()}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="bg-gray-50 border border-gray-200 rounded-xl p-6">
                    <div className="flex items-center">
                      <Clock size={24} className="text-gray-500 mr-4" />
                      <p className="text-gray-600 text-lg">{translations[language].finalVerdictPending}</p>
                    </div>
                  </div>
                )}
              </div>

              {/* Attached Documents Section */}
              <div className="mb-8">
                <h4 className="text-xl font-bold mb-6 text-gray-800 flex items-center">
                  <Paperclip className="w-6 h-6 mr-3 text-gray-600" />
                 {translations[language].caseDocuments}
                </h4>
                {viewHearing.documents && viewHearing.documents.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {viewHearing.documents.map((doc, index) => (
                      <div key={index} className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-4 flex items-center justify-between shadow-sm">
                        <div className="flex items-center">
                          <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center mr-3">
                            <FileText size={20} className="text-white" />
                          </div>
                          <div>
                            <span className="font-medium text-gray-800">{doc.document_name}</span>
                            {doc.document_name.toLowerCase().includes('verdict') && (
                              <span className="block text-xs text-green-600 font-medium">{translations[language].finalVerdictLabel}</span>
                            )}
                          </div>
                        </div>
                        <button
                          onClick={() => window.open(doc.upload_path, '_blank')}
                          className="text-blue-600 hover:text-blue-800 p-2 rounded-lg hover:bg-blue-100 transition-colors"
                        >
                          <Download size={16} />
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="bg-gray-50 rounded-xl p-6 text-center">
                    <FileText className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                    <p className="text-gray-500">{translations[language].noDocumentsAttached}</p>
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end space-x-4 mt-8 pt-6 border-t">
                <button
                  onClick={() => {
                    setShareModalOpen(true);
                    setShareModalSource("completed");
                    setShowViewCompletedModal(false);
                  }}
                  className="bg-gradient-to-r from-blue-600 to-blue-900 text-white flex items-center px-6 py-3 rounded-2xl hover:from-blue-900 hover:to-blue-900 font-semibold transition-all duration-300 shadow-lg hover:shadow-xl"
                >
                  <Share2 size={18} className="mr-2" />
                  {translations[language].shareDetails}
                </button>
                <button
                  onClick={() => setShowViewCompletedModal(false)}
                  className="border border-gray-300 text-gray-700 px-6 py-3 rounded-2xl hover:bg-gray-100 font-semibold transition-all duration-300"
                >
                  {translations[language].close}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Always render hidden printable notice if it's been generated */}
      {noticeText && (
        <div id="printable-notice" style={{ display: 'none' }}>
          <Section11Notice noticeText={noticeText} />
        </div>
      )}

    </div>
  );
}